import { AppStateModel } from '../State/AppStateModel';
import { SocketProvider } from '../Socket/SocketProvider';
import { IAppState } from '../State/IAppState';
import { IUserState } from '../State/IUserState';
import { AppSections } from '../State/AppSections';
import { ILoginConflictSession, ILoginConflictState } from '../State/ILoginConflictState';
import { IRoomState } from '../State/IRoomState';
import { AutoActionIntent, IGameplayEvent, IJoinedRoomState, JoinedRoomStateFactory } from '../State/IJoinedRoomState';
import { resetQueryClient } from '../Query/QueryClient';
import { ActivityMessageData, MessageType } from '../../Utils/TemplateUtil';
import { NotificationTemplateUtil, NotificationData } from '../../Utils/NotificationTemplateUtil.tsx';
import { hasReservedWindow, reserveDetachedWindow, transferDetachedWindow } from '../../Utils/DetachedWindowManager';
import { notificationFrequencyManager } from '../../Utils/NotificationFrequencyManager';
import { UserSettingsManager } from '../../Utils/UserSettingsManager.ts';
import {
    applyTournamentEliminationStatusToRoom,
    isTournamentEliminationNotification
} from '../../Utils/TournamentEliminationNotificationUtils';
import { ICasinoGameSession } from '../../Casino/Interface/ICasinoGameSession';
import { CasinoConfig } from '../../Casino/CasinoConfig';
import i18n, { t, loadNamespace } from '../../i18n';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { isPopupDebugEnabled } from '../../Utils/PopupDebug';
import { windowFocusManager } from '../../Utils/WindowFocusManager';
import {
    clearAutoLoginSuppression,
    removeAuthTokenFromUrl,
    suppressAutoLoginForSession
} from '../../Utils/AuthAutoLogin';
import { openCasinoDetachedWindow, unregisterCasinoDetachedWindow } from '../../Utils/CasinoDetachedWindow';
import { roomGameplayEventBus } from '../Events/RoomGameplayEventBus';
import { roomTableReactionEventBus } from '../Events/RoomTableReactionEventBus';
import {
    ITableReactionEvent,
    ITableReactionRejected,
    TABLE_REACTION_IDS,
    TABLE_REACTION_REJECT_REASONS,
    TableReactionId,
    TableReactionRejectedReason
} from '../State/ITableReactionState';
const SUPPORTED_GAMEPLAY_EVENT_KINDS: readonly IGameplayEvent['kind'][] = [
    'handStarted',
    'playerAction',
    'turnStarted',
    'streetDealt',
    'showdownStarted',
    'showdownCardsRevealed',
    'winnerDeclared',
    'handEnded',
    'timerWarning'
];

const SUPPORTED_GAMEPLAY_EVENT_STREETS: readonly NonNullable<IGameplayEvent['street']>[] = [
    'flop',
    'turn',
    'river'
];

const STREET_DEALT_PAYLOAD_REQUIREMENTS: Record<NonNullable<IGameplayEvent['street']>, {
    cardsCount: number;
    communityCardsCount: number;
}> = {
    flop: { cardsCount: 3, communityCardsCount: 3 },
    turn: { cardsCount: 1, communityCardsCount: 4 },
    river: { cardsCount: 1, communityCardsCount: 5 }
};

const SUPPORTED_GAMEPLAY_EVENT_KIND_SET = new Set<string>(SUPPORTED_GAMEPLAY_EVENT_KINDS);
const SUPPORTED_GAMEPLAY_EVENT_STREET_SET = new Set<string>(SUPPORTED_GAMEPLAY_EVENT_STREETS);
type LobbyType = 'normal' | 'tournament' | 'tournament_rooms' | 'tournament_rankings';

const isSupportedGameplayEventKind = (kind: string): kind is IGameplayEvent['kind'] => {
    return SUPPORTED_GAMEPLAY_EVENT_KIND_SET.has(kind);
};

const isSupportedGameplayEventStreet = (
    street: string | null
): street is NonNullable<IGameplayEvent['street']> => {
    return typeof street === 'string' && SUPPORTED_GAMEPLAY_EVENT_STREET_SET.has(street);
};

const SUPPORTED_TABLE_REACTION_ID_SET = new Set<string>(TABLE_REACTION_IDS);
const isSupportedTableReactionId = (reactionId: string | null): reactionId is TableReactionId => {
    return typeof reactionId === 'string' && SUPPORTED_TABLE_REACTION_ID_SET.has(reactionId);
};

const SUPPORTED_TABLE_REACTION_REJECT_REASON_SET = new Set<string>(TABLE_REACTION_REJECT_REASONS);
const isSupportedTableReactionRejectedReason = (
    reason: string | null
): reason is TableReactionRejectedReason => {
    return typeof reason === 'string' && SUPPORTED_TABLE_REACTION_REJECT_REASON_SET.has(reason);
};

// Tournament notification icons
const TOURNAMENT_ICONS = {
    trophy: '/images/notification/tournament-trophy.svg',
    blinds: '/images/notification/tournament-blinds.svg',
    eliminated: '/images/notification/tournament-eliminated.svg',
    pause: '/images/notification/tournament-pause.svg',
    resume: '/images/notification/tournament-resume.svg',
    success: '/images/notification/tournament-success.svg'
};

interface RoomProtocolSnapshot {
    roomId: string;
    gameId: string;
    handId: string;
    stateVersion: number;
    lastAppliedGameplaySequence: number;
    serverTime?: string;
    state: any;
}

interface RoomProtocolCache {
    roomId: string;
    gameId: string;
    handId: string;
    stateVersion: number;
    lastGameplayEventSequence: number;
    lastAppliedGameplaySequence: number;
    seenGameplayEventIds: string[];
    playerIds: Set<string>;
}

type AvatarUploadRequest = {
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: number;
};

export class InteractionHandler {

    private model: AppStateModel;
    private connection: SocketProvider | null;
    private enqueueSnackbar: (message: string, options?: any) => void;
    private closeSnackbar: ((key?: any) => void) | null = null;
    private reconnectSnackbarKey: any | null = null;
    public onTournamentRoomTransfer?: (oldRoomId: string, newRoomId: string) => void;
    public userSettingsManager: UserSettingsManager;
    private roomProtocolCaches: Map<string, RoomProtocolCache> = new Map();
    private avatarUploadRequests: Map<string, AvatarUploadRequest> = new Map();
    private static readonly AVATAR_UPLOAD_TIMEOUT_MS = 15000;
    private static readonly AVATAR_MAX_BYTES = 3 * 1024 * 1024;
    private static readonly AVATAR_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/bmp', 'image/gif']);

    constructor(model: AppStateModel, connection: SocketProvider | null, enqueueSnackbar: (message: string, options?: any) => void) {
        this.model = model;
        this.connection = connection;
        this.enqueueSnackbar = enqueueSnackbar;
        this.userSettingsManager = new UserSettingsManager(connection);
    }

    public setEnqueueSnackbar = (enqueueSnackbar: (message: string, options?: any) => void) => {
        this.enqueueSnackbar = enqueueSnackbar;
    }

    private showNotification = (message: string, options: any = {}, bypassFrequency: boolean = false) => {
        if (bypassFrequency || notificationFrequencyManager.shouldAllowNotification()) {
            if (!bypassFrequency) {
                notificationFrequencyManager.recordNotification();
            }
            this.enqueueSnackbar(message, options);
        }
    }

    private getProtocolString = (value: unknown): string | null => {
        return typeof value === 'string' && value.length > 0 ? value : null;
    }

    private getProtocolNumber = (value: unknown): number | null => {
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
    }

    private getProtocolSnapshot = (data: any, stateKey: 'state' | 'roomState'): RoomProtocolSnapshot | null => {
        const roomId = this.getProtocolString(data?.roomId);
        const gameId = this.getProtocolString(data?.gameId);
        const handId = this.getProtocolString(data?.handId);
        const stateVersion = this.getProtocolNumber(data?.stateVersion);
        const lastAppliedGameplaySequence = this.getProtocolNumber(data?.lastAppliedGameplaySequence);
        const state = data?.[stateKey];

        if (!roomId || !gameId || !handId || stateVersion === null || lastAppliedGameplaySequence === null || !state || typeof state !== 'object') {
            console.error(`[room-protocol-v2] Invalid ${stateKey} snapshot`, data);
            if (roomId) {
                this.requestRoomStateForProtocolError(roomId, `Invalid ${stateKey} snapshot`, data);
            }
            return null;
        }

        return {
            roomId,
            gameId,
            handId,
            stateVersion,
            lastAppliedGameplaySequence,
            serverTime: this.getProtocolString(data?.serverTime) || undefined,
            state
        };
    }

    private getProtocolCache = (roomId: string): RoomProtocolCache | null => {
        return this.roomProtocolCaches.get(roomId) || null;
    }

    private seedProtocolCacheFromSnapshot = (snapshot: RoomProtocolSnapshot, state: any) => {
        const players = Array.isArray(state?.players) ? state.players : [];
        this.roomProtocolCaches.set(snapshot.roomId, {
            roomId: snapshot.roomId,
            gameId: snapshot.gameId,
            handId: snapshot.handId,
            stateVersion: snapshot.stateVersion,
            lastGameplayEventSequence: snapshot.lastAppliedGameplaySequence,
            lastAppliedGameplaySequence: snapshot.lastAppliedGameplaySequence,
            seenGameplayEventIds: [],
            playerIds: new Set(players.map((player: any) => this.getProtocolString(player?.userId)).filter(Boolean) as string[])
        });
    }

    private rememberProtocolGameplayEvent = (cache: RoomProtocolCache, eventId: string, sequence: number) => {
        cache.lastGameplayEventSequence = sequence;
        cache.seenGameplayEventIds = [...cache.seenGameplayEventIds.filter((id) => id !== eventId), eventId].slice(-200);
    }

    private requestRoomStateForProtocolError = (roomId: string | null | undefined, reason: string, payload: unknown) => {
        if (!roomId) {
            console.error(`[room-protocol-v2] ${reason}`, payload);
            return;
        }
        console.error(`[room-protocol-v2] ${reason}`, payload);
        this.requestRoomState(roomId);
    }

    public setCloseSnackbar = (closeSnackbar: (key?: any) => void) => {
        this.closeSnackbar = closeSnackbar;
    }

    public setDetachedRooms = (_detachedRooms: { [roomId: string]: boolean }) => {
    }

    /**
     * Creates a custom notification content with a specific tournament icon
     * Returns an object with content property that can be spread into enqueueSnackbar options
     */
    private createTournamentNotification = (message: string, iconType: keyof typeof TOURNAMENT_ICONS) => {
        const iconSrc = TOURNAMENT_ICONS[iconType];
        return {
            content: (_key: string, _message: string) => {
                return React.createElement('div', {
                    style: { display: 'flex', alignItems: 'center' }
                }, [
                    React.createElement('img', {
                        key: 'icon',
                        src: iconSrc,
                        alt: t('Notifications:tournament.defaultName'),
                        width: 12,
                        height: 12,
                        style: { marginRight: '0.5rem' }
                    }),
                    React.createElement('span', { key: 'text' }, message)
                ]);
            }
        };
    }

    public login = (username: string, password: string) => {
        clearAutoLoginSuppression();
        this.model.resetState();
        this.connection?.logIn(username, password);
    }

    public logout = () => {
        this.connection?.logOut(this.model.app.state.user.id);
        this.model.resetState();
        this.connection?.closeConnection();
    }

    public loadIntoState = (data: IAppState) => {
        this.model.app.setState({ ...data });
    }

    public getCurrentSection = (): string => {
        return this.model.app.state.user.section;
    }

    public getJoinedRoomIds = (): string[] => {
        return this.model.app.state.joinedRooms.map((room) => room.roomId);
    }

    public getSingleViewRoomId = (): string | null => {
        return this.model.app.state.user.singleViewRoomId;
    }

    public isAuthenticated = (): boolean => {
        return Boolean(this.model.app.state.user.authenticated);
    }

    private isNormalLobbySection = (sec: string): boolean =>
        sec === AppSections.LOBBY_AOF_CRYPTO ||
        sec === AppSections.LOBBY_AOF_FIAT ||
        sec === AppSections.LOBBY_TEXAS_CASH;

    private getLobbyTypeFromSection = (section: string): LobbyType | null => {
        if (section === AppSections.LOBBY_HOME) return 'normal';
        if (section === AppSections.LOBBY_TOURNAMENT) return 'tournament';
        if (section === AppSections.LOBBY_TOURNAMENT_ROOMS) return 'tournament_rooms';
        if (section === AppSections.LOBBY_TOURNAMENT_RANKINGS) return 'tournament_rankings';
        if (this.isNormalLobbySection(section)) return 'normal';
        return null;
    }

    private joinLobbyType = (lobbyType: LobbyType) => {
        if (lobbyType === 'tournament') return;
        this.connection?.joinLobby(lobbyType);
    };

    public setSection = (section: any) => {
        const oldSection = this.model.app.state.user.section;
        const newSection = section.startsWith(AppSections.POKER_SINGLE_ROOM_VIEW)
            ? AppSections.POKER_SINGLE_ROOM_VIEW
            : section.startsWith(AppSections.CASINO_GAME_VIEW)
                ? AppSections.CASINO_GAME_VIEW
                : section;

        const oldLobbyType = this.getLobbyTypeFromSection(oldSection);
        const newLobbyType = this.getLobbyTypeFromSection(newSection);

        // --- Update State First ---
        if (section.startsWith(`${AppSections.POKER_SINGLE_ROOM_VIEW}:`)) {
            const roomId = section.split(':')[1];
            // Update section + selected room atomically to avoid transient/partial states
            // (e.g. section === poker_single_room_view while singleViewRoomId is null).
            this.model.app.setState(prevState => ({
                user: {
                    ...prevState.user,
                    section: AppSections.POKER_SINGLE_ROOM_VIEW,
                    singleViewRoomId: roomId || null,
                }
            }));
        } else if (section.startsWith(`${AppSections.CASINO_GAME_VIEW}:`)) {
            const sessionId = section.split(':')[1];
            this.model.app.setState(prevState => ({
                user: {
                    ...prevState.user,
                    section: AppSections.CASINO_GAME_VIEW,
                    singleViewCasinoGameId: sessionId || null,
                }
            }));
        } else {
            // Clear single view IDs and update section atomically to avoid transient states where
            // section === poker_single_room_view but singleViewRoomId is null ("No room selected" screen).
            this.model.app.setState(prevState => ({
                user: {
                    ...prevState.user,
                    section: newSection,
                    singleViewRoomId:
                        newSection === AppSections.POKER_SINGLE_ROOM_VIEW ? prevState.user.singleViewRoomId : null,
                    singleViewCasinoGameId:
                        newSection === AppSections.CASINO_GAME_VIEW ? prevState.user.singleViewCasinoGameId : null,
                }
            }));
        }

        // --- Handle Lobby Joining/Leaving ---
        // Case 1: Moving between different lobby types (normal <-> tournament)
        if (newLobbyType && oldLobbyType && newLobbyType !== oldLobbyType) {
            this.connection?.leaveLobby(oldLobbyType); // Leave the old lobby type
            if (newLobbyType !== 'tournament') {
                this.joinLobbyType(newLobbyType);  // Join the new lobby type
            }
        }
        // Case 2: Entering a lobby section from a non-lobby section
        else if (newLobbyType && !oldLobbyType) {
            if (newLobbyType !== 'tournament') {
                this.joinLobbyType(newLobbyType); // Join the new lobby type
            }
        }
        // Case 3: Leaving a lobby section for a non-lobby section
        else if (!newLobbyType && oldLobbyType) {
            this.connection?.leaveLobby(oldLobbyType); // Leave the old lobby type
        }
        // Case 4: Moving within the same lobby type (e.g., AoF -> Cash) - Do nothing.
        // Case 5: Moving between non-lobby sections (e.g., Login -> Game Room) - Do nothing.
    };

    public toggleProfileModal = () => {
        this.model.app.setState(prevState => ({
            user: {
                ...prevState.user,
                isProfileOpen: !prevState.user.isProfileOpen
            }
        }));
    };

    public setUserState = (data: IUserState) => {
        const tickets = Array.isArray((data as any)?.tickets)
            ? (data as any).tickets
            : (this.model.app.state.user as any)?.tickets || [];

        const userData = {
            ...this.model.app.state.user,
            ...data,
            section: data.section || AppSections.LOBBY_AOF_FIAT,
            authenticated: Boolean(data.authenticated),
            balance: Number(data.balance) || 0,
            tickets
        } as IUserState & { tickets?: any[] };

        if (userData.pokerUserSettings) {
            this.userSettingsManager.loadSettings(userData.pokerUserSettings, true);
        }

        this.model.app.setState({
            user: userData,
            connected: true,
            isLoading: false,
            loginConflict: null
        });
    }

    public uploadAvatar = (file: File): Promise<void> => {
        const notifyAvatarUploadError = (message: string) => {
            this.showNotification(message, {
                variant: 'error',
                autoHideDuration: 5000
            }, true);
        };

        const validationError = this.validateAvatarFile(file);
        if (validationError) {
            notifyAvatarUploadError(validationError);
            return Promise.reject(new Error(validationError));
        }

        if (!this.connection) {
            const message = 'Not connected to poker server.';
            notifyAvatarUploadError(message);
            return Promise.reject(new Error(message));
        }

        const requestId = `avatar-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

        return new Promise((resolve, reject) => {
            const clearRequest = () => {
                const pending = this.avatarUploadRequests.get(requestId);
                if (pending) {
                    window.clearTimeout(pending.timeoutId);
                    this.avatarUploadRequests.delete(requestId);
                }
            };
            const rejectAndClear = (error: Error) => {
                clearRequest();
                reject(error);
            };

            const timeoutId = window.setTimeout(() => {
                const error = new Error('Avatar upload timed out.');
                this.avatarUploadRequests.delete(requestId);
                notifyAvatarUploadError(error.message);
                reject(error);
            }, InteractionHandler.AVATAR_UPLOAD_TIMEOUT_MS);

            this.avatarUploadRequests.set(requestId, { resolve, reject, timeoutId });

            const reader = new FileReader();
            reader.onerror = () => {
                const error = new Error('Avatar file could not be read.');
                notifyAvatarUploadError(error.message);
                rejectAndClear(error);
            };
            reader.onload = () => {
                const result = typeof reader.result === 'string' ? reader.result : '';
                const contentBase64 = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;

                if (!contentBase64) {
                    const error = new Error('Avatar file could not be read.');
                    notifyAvatarUploadError(error.message);
                    rejectAndClear(error);
                    return;
                }

                this.connection?.send('player', 'updateAvatar', {
                    requestId,
                    filename: file.name || 'avatar',
                    mimeType: file.type,
                    contentBase64
                });
            };
            reader.readAsDataURL(file);
        });
    };

    public handleAvatarUpdate = (data: any) => {
        const avatarUrl = typeof data?.avatarUrl === 'string' ? data.avatarUrl.trim() : '';
        if (!avatarUrl) return;

        const userId = typeof data?.userId === 'string' && data.userId.trim().length > 0
            ? data.userId.trim()
            : this.model.app.state.user.id;
        const requestId = typeof data?.requestId === 'string' ? data.requestId : undefined;

        this.model.app.setState((prevState) => ({
            user: userId === prevState.user.id
                ? { ...prevState.user, avatarUrl }
                : prevState.user,
            joinedRooms: prevState.joinedRooms.map((room) => ({
                ...room,
                players: Array.isArray(room.players)
                    ? room.players.map((player) => (
                        player.userId === userId ? { ...player, avatarUrl } : player
                    ))
                    : room.players
            })),
        }));

        if (requestId) {
            const pending = this.avatarUploadRequests.get(requestId);
            if (pending) {
                window.clearTimeout(pending.timeoutId);
                this.avatarUploadRequests.delete(requestId);
                pending.resolve();
                this.showNotification(t('Notifications:settings.updatedSuccessfully'), {
                    variant: 'success',
                    autoHideDuration: 3000
                }, true);
            }
        }
    };

    public handleAvatarUpdateFailed = (data: any) => {
        const requestId = typeof data?.requestId === 'string' ? data.requestId : undefined;
        const message = typeof data?.message === 'string' && data.message.trim().length > 0
            ? data.message.trim()
            : 'Avatar upload failed.';

        if (requestId) {
            const pending = this.avatarUploadRequests.get(requestId);
            if (pending) {
                window.clearTimeout(pending.timeoutId);
                this.avatarUploadRequests.delete(requestId);
                pending.reject(new Error(message));
            }
        }

        this.showNotification(message, {
            variant: 'error',
            autoHideDuration: 5000
        }, true);
    };

    private validateAvatarFile(file: File): string | null {
        if (!file) {
            return 'Avatar file is required.';
        }
        if (!InteractionHandler.AVATAR_MIME_TYPES.has(file.type)) {
            return 'Only jpg, png, bmp and gif avatar images are allowed.';
        }
        if (file.size > InteractionHandler.AVATAR_MAX_BYTES) {
            return 'Avatar file exceeds 3 Megabytes.';
        }

        return null;
    }

    private normalizeLoginConflictSession = (session: any): ILoginConflictSession | undefined => {
        if (!session || typeof session !== 'object') return undefined;
        return {
            clientId: typeof session.clientId === 'string' ? session.clientId : undefined,
            userId: typeof session.userId === 'string' ? session.userId : undefined,
            username: typeof session.username === 'string' ? session.username : undefined,
            ipAddress: typeof session.ipAddress === 'string' ? session.ipAddress : undefined,
            forwardedFor: typeof session.forwardedFor === 'string' ? session.forwardedFor : undefined,
            countryCode: typeof session.countryCode === 'string' ? session.countryCode : undefined,
            userAgent: typeof session.userAgent === 'string' ? session.userAgent : undefined,
            deviceLabel: typeof session.deviceLabel === 'string' ? session.deviceLabel : undefined,
            connectedAt: typeof session.connectedAt === 'string' ? session.connectedAt : undefined,
            lastActivityAt: typeof session.lastActivityAt === 'string' ? session.lastActivityAt : undefined,
        };
    }

    public handleLoginConflict = (data: any) => {
        const conflictId = typeof data?.conflictId === 'string' ? data.conflictId : '';
        if (!conflictId) return;

        suppressAutoLoginForSession();

        const existingSessions = Array.isArray(data?.existingSessions)
            ? data.existingSessions
                .map((session: any) => this.normalizeLoginConflictSession(session))
                .filter((session: ILoginConflictSession | undefined): session is ILoginConflictSession => Boolean(session))
            : [];

        const loginConflict: ILoginConflictState = {
            authenticated: false,
            reason: typeof data?.reason === 'string' ? data.reason : 'same_user_already_connected',
            message: typeof data?.message === 'string' ? data.message : undefined,
            conflictId,
            expiresAt: typeof data?.expiresAt === 'string' ? data.expiresAt : undefined,
            user: data?.user && typeof data.user === 'object'
                ? {
                    id: typeof data.user.id === 'string' ? data.user.id : undefined,
                    username: typeof data.user.username === 'string' ? data.user.username : undefined,
                }
                : undefined,
            existingSessions,
            requestingSession: this.normalizeLoginConflictSession(data?.requestingSession),
        };

        this.model.app.setState((prevState) => ({
            isLoading: false,
            connected: true,
            loginConflict,
            user: {
                ...prevState.user,
                authenticated: false,
                hasCredentialError: false,
                hasFocedLogoutMaxClients: false,
            },
        }) as Pick<IAppState, 'isLoading' | 'connected' | 'loginConflict' | 'user'>);
    }

    public confirmLoginTakeover = (conflictId: string) => {
        if (!conflictId) return;
        this.model.app.setState((prevState) => ({
            loginConflict: prevState.loginConflict
                ? { ...prevState.loginConflict, isSubmitting: true, submitAction: 'confirm' }
                : prevState.loginConflict,
        }) as Pick<IAppState, 'loginConflict'>);
        this.connection?.confirmLoginTakeover(conflictId);
    }

    public cancelLoginTakeover = (conflictId: string) => {
        if (!conflictId) return;
        suppressAutoLoginForSession();
        removeAuthTokenFromUrl();
        localStorage.removeItem('sessionToken');
        void resetQueryClient();
        this.model.app.setState((prevState) => ({
            loginConflict: prevState.loginConflict
                ? { ...prevState.loginConflict, isSubmitting: true, submitAction: 'cancel' }
                : prevState.loginConflict,
        }) as Pick<IAppState, 'loginConflict'>);
        this.connection?.cancelLoginTakeover(conflictId);
    }

    public handleLoginTakeoverCancelled = (data: any) => {
        suppressAutoLoginForSession();
        removeAuthTokenFromUrl();
        localStorage.removeItem('sessionToken');
        void resetQueryClient();
        this.model.resetState();
        const message = typeof data?.message === 'string'
            ? data.message
            : 'Login cancelled. The existing session remains active.';
        this.showNotification(message, {
            variant: 'info',
            autoHideDuration: 5000
        }, true);
    }

    public handleLoginConflictExpired = (data: any) => {
        suppressAutoLoginForSession();
        removeAuthTokenFromUrl();
        localStorage.removeItem('sessionToken');
        void resetQueryClient();
        this.model.resetState();
        const message = typeof data?.message === 'string'
            ? data.message
            : 'Login request expired. Please log in again.';
        this.showNotification(message, {
            variant: 'warning',
            autoHideDuration: 6000
        }, true);
    }

    public showPersistentReconnectToast = async () => {
        try {
            if (!this.enqueueSnackbar) return;
            if (this.reconnectSnackbarKey) return;
            // Ensure Notifications namespace is loaded before showing toast
            await loadNamespace('Notifications');
            const key = this.enqueueSnackbar(t('Notifications:connection.lost'), {
                variant: 'info',
                persist: true,
            });
            this.reconnectSnackbarKey = key;
        } catch {}
    }

    public closeReconnectToastAndShowReconnected = async () => {
        try {
            if (this.reconnectSnackbarKey && this.closeSnackbar) {
                this.closeSnackbar(this.reconnectSnackbarKey);
                this.reconnectSnackbarKey = null;
            }
            // Ensure Notifications namespace is loaded before showing toast
            await loadNamespace('Notifications');
            this.showNotification(t('Notifications:connection.reestablished'), {
                variant: 'success',
                autoHideDuration: 3000
            });
        } catch {}
    }

    // Rejoin lobby and previously open rooms after successful login with token
    public rejoinSubscriptionsAfterAuth = () => {
        const currentSection = this.model.app.state.user.section;
        // If we're on LOGIN (initial login), join the main cash lobby data used by Home.
        if (currentSection === AppSections.LOGIN) {
            this.connection?.joinLobby('normal');
        } else {
            // Otherwise, join the appropriate lobby type
            const lobbyType = this.getLobbyTypeFromSection(currentSection) || 'normal';
            this.joinLobbyType(lobbyType);
        }

        const rooms = this.model.app.state.joinedRooms;
        rooms.forEach((room) => {
            if (room?.roomId) {
                this.connection?.joinRoom(room.roomId);
            }
        });
    }

    public setSingleViewRoomId = (roomId: string, callback?: () => void) => {
        this.model.app.setState(prevState => ({
            user: {
                ...prevState.user,
                singleViewRoomId: roomId
            }
        }), callback);
    };

    public clearSingleViewRoomId = () => {
        this.model.app.setState(prevState => ({
            user: { ...prevState.user, singleViewRoomId: null }
        }));
    };

    public setSingleViewCasinoGameId = (sessionId: string, callback?: () => void) => {
        this.model.app.setState(prevState => ({
            user: {
                ...prevState.user,
                singleViewCasinoGameId: sessionId
            }
        }), callback);
    };

    public clearSingleViewCasinoGameId = () => {
        this.model.app.setState(prevState => ({
            user: { ...prevState.user, singleViewCasinoGameId: null }
        }));
    };

    // Track detached casino windows
    private detachedCasinoWindows: Map<string, Window> = new Map();

    public openCasinoGameSession = (session: ICasinoGameSession) => {
        // If configured to launch in detached window, open immediately before adding to state
        if (CasinoConfig.launchInDetachedWindow) {
            const newWindow = openCasinoDetachedWindow(session);

            if (newWindow) {
                this.detachedCasinoWindows.set(session.sessionId, newWindow);

                // Poll to detect when window is closed
                const checkInterval = window.setInterval(() => {
                    if (newWindow.closed) {
                        clearInterval(checkInterval);
                        this.detachedCasinoWindows.delete(session.sessionId);
                        unregisterCasinoDetachedWindow(session.sessionId);
                        this.closeCasinoGameSession(session.sessionId);
                    }
                }, 500);
            }

            // Add to state but don't navigate to game view
            this.model.app.setState((prevState) => {
                const exists = prevState.casinoSessions.some((s) => s.sessionId === session.sessionId);
                const casinoSessions = exists
                    ? prevState.casinoSessions.map((s) => (s.sessionId === session.sessionId ? session : s))
                    : [...prevState.casinoSessions, session];
                return { casinoSessions };
            });
            return;
        }

        // Original behavior: open in iframe within main window
        let shouldNavigate = true;
        this.model.app.setState((prevState) => {
            const exists = prevState.casinoSessions.some((s) => s.sessionId === session.sessionId);
            shouldNavigate = !exists;
            const casinoSessions = exists
                ? prevState.casinoSessions.map((s) => (s.sessionId === session.sessionId ? session : s))
                : [...prevState.casinoSessions, session];
            return { casinoSessions };
        }, () => {
            if (shouldNavigate) {
                this.setSection(`${AppSections.CASINO_GAME_VIEW}:${session.sessionId}`);
            }
        });
    };

    public closeCasinoGameSession = (sessionId: string) => {
        // Close detached window if exists
        const detachedWindow = this.detachedCasinoWindows.get(sessionId);
        if (detachedWindow && !detachedWindow.closed) {
            detachedWindow.close();
        }
        this.detachedCasinoWindows.delete(sessionId);
        unregisterCasinoDetachedWindow(sessionId);

        const wasActive =
            this.model.app.state.user.section === AppSections.CASINO_GAME_VIEW &&
            this.model.app.state.user.singleViewCasinoGameId === sessionId;

        this.model.app.setState((prevState) => ({
            casinoSessions: prevState.casinoSessions.filter((s) => s.sessionId !== sessionId)
        }), () => {
            if (!wasActive) return;
            const remaining = this.model.app.state.casinoSessions;
            if (remaining.length > 0) {
                this.setSection(`${AppSections.CASINO_GAME_VIEW}:${remaining[0].sessionId}`);
            } else {
                this.setSection(AppSections.CASINO);
            }
        });
    };

    public joinRoom = (
        roomId: string,
        seatIndex?: number,
        sourceWindow?: Window | null
    ) => {
        if (this.connection) {
            const isAlreadyInRoom = this.model.app.state.joinedRooms.some(room => room.roomId === roomId);
            if (isPopupDebugEnabled()) {
                console.debug('[joinRoom] Requested', { roomId, seatIndex, isAlreadyInRoom, isMobile });
            }
            if (!isMobile) {
                // If the room is already joined and open in a detached popup, focus it synchronously.
                // Passing `sourceWindow` preserves user activation when the click originated in a detached lobby.
                if (isAlreadyInRoom && windowFocusManager.focusWindow(roomId, sourceWindow)) {
                    return;
                }

                // In normal operation we pre-open a blank window on user gesture (reduces popup blocking).
                if (!isAlreadyInRoom && !hasReservedWindow(roomId)) {
                    reserveDetachedWindow(roomId, undefined, sourceWindow);
                }
            }
            if (isAlreadyInRoom) {
                this.handleAlreadyInRoom(roomId, seatIndex, sourceWindow);
            } else {
                this.handleJoinNewRoom(roomId, seatIndex);
            }
        } else {
            console.error(`Connection not established. Cannot join room.`);
        }
    }

    private handleAlreadyInRoom = (roomId: string, seatIndex?: number, sourceWindow?: Window | null) => {
        // Prefer focusing an existing detached window if one exists; otherwise navigate in the main client.
        if (!isMobile && windowFocusManager.focusWindow(roomId, sourceWindow)) {
            return;
        }
        this.switchToRoomInMainView(roomId, seatIndex);
    }

    private switchToRoomInMainView = (roomId: string, seatIndex?: number) => {
        const currentSection = this.model.app.state.user.section;
        const currentLobbyType = this.getLobbyTypeFromSection(currentSection);
        if (currentLobbyType) {
            this.connection?.leaveLobby(currentLobbyType);
        }
        this.setSingleViewRoomId(roomId, () => {
            this.setSection(AppSections.POKER_SINGLE_ROOM_VIEW);
            if (seatIndex !== undefined) {
                this.requestSitOptions(roomId, seatIndex);
            }
        });
    }

    private handleJoinNewRoom = (roomId: string, seatIndex?: number) => {
        const currentSection = this.model.app.state.user.section;
        const currentLobbyType = this.getLobbyTypeFromSection(currentSection);
        if (currentLobbyType) {
            this.connection?.leaveLobby(currentLobbyType);
        }
        this.connection?.joinRoom(roomId);

        // Avoid temporarily switching the main table view to a room that isn't joined yet.
        // This prevents showing "No room selected" when joining a new room while already in table view.
        const isCurrentlyInSingleRoomView = currentSection === AppSections.POKER_SINGLE_ROOM_VIEW;
        const shouldUpdateSingleView = isMobile || !isCurrentlyInSingleRoomView;

        if (shouldUpdateSingleView) {
            this.setSingleViewRoomId(roomId, () => {
                if (seatIndex !== undefined) {
                    this.requestSitOptions(roomId, seatIndex);
                }
            });
        } else if (seatIndex !== undefined) {
            this.requestSitOptions(roomId, seatIndex);
        }
    }

    public setUserConnectedCount = (count: number) => {
        this.model.app.setState((prevState) => ({
            ...prevState,
            userConnectedCount: count
        }));
    }

    public sendChatMessage = (message: string) => {
        this.connection?.send('lobby', 'chatMessage', message);
    }

    public requestLeaveRoom = (roomId: string) => {
        this.connection?.leaveRoom(roomId);
        // setTimeout(() => {
        //     const roomStillExists = this.model.app.state.joinedRooms.some(room => room.roomId === roomId);
        //     if (roomStillExists) {
        //         this.removeJoinedRoom(roomId);
        //         this.clearSingleViewRoomId();
        //         if (this.isUserInSection(AppSections.POKER_SINGLE_ROOM_VIEW)) {
        //             this.connection?.joinLobby('normal');
        //             this.setSection(AppSections.LOBBY_AOF_FIAT);
        //         }
        //     }
        // }, 400);
    };

    public switchToLobby = () => {
        this.setSection(AppSections.LOBBY_HOME);
    }

    public requestSitOnRoom = (roomId: string, seatIndex: number, buyInAmount: number) => {
        this.connection?.sitOnRoom(roomId, seatIndex, buyInAmount);
    }

    public requestStandFromRoom = (roomId: string) => {
        this.connection?.standFromRoom(roomId);
    }

    public setConnected = (connected: boolean) => {
        this.model.app.setState({ connected });
    }

    public setUserLoginCredentialError = (hasCredentialError: boolean) => {
        this.model.app.setState({
            user: {
                ...this.model.app.state.user,
                hasCredentialError
            }
        });
    }

    public setForcedLogoutMaxClients = (hasForcedLogout: boolean) => {
        this.model.app.setState({
            user: {
                ...this.model.app.state.user,
                hasFocedLogoutMaxClients: hasForcedLogout
            }
        });
    }

    private normalizeHomeLobbyTournament = (tournament: any, isUserRegistered: boolean) => ({
        ...tournament,
        isUserRegistered,
        registeredPlayers: Array.isArray(tournament?.registeredPlayers) ? tournament.registeredPlayers : [],
        playerList: Array.isArray(tournament?.playerList) ? tournament.playerList : [],
        tables: Array.isArray(tournament?.tables) ? tournament.tables : [],
        blindStructureSnapshot: Array.isArray(tournament?.blindStructureSnapshot) ? tournament.blindStructureSnapshot : [],
        rankings: Array.isArray(tournament?.rankings) ? tournament.rankings.map((ranking: any) => ({
            ...ranking,
            eliminationTime: ranking.eliminationTime || null,
            registrationTime: ranking.registrationTime || null,
            chipsBeforeElimination: typeof ranking.chipsBeforeElimination === 'number' ? ranking.chipsBeforeElimination : null
        })) : [],
        activeRooms: Array.isArray(tournament?.activeRooms) ? tournament.activeRooms : []
    });

    private mergeHomeLobbyTournaments = (registeredTournaments: any[], upcomingTournaments: any[]) => {
        const byId = new Set<string>();
        const merged: any[] = [];

        registeredTournaments.forEach((tournament) => {
            const id = tournament?.gameId || tournament?.tournamentId;
            if (!id || byId.has(id)) return;
            byId.add(id);
            merged.push(tournament);
        });

        upcomingTournaments.forEach((tournament) => {
            const id = tournament?.gameId || tournament?.tournamentId;
            if (!id || byId.has(id)) return;
            byId.add(id);
            merged.push(tournament);
        });

        return merged;
    };

    public updateLobbyState = (data: any) => {
        // Check for tournament_rankings FIRST before checking hasTournamentList
        if (data.type === 'tournament_rankings') {
            this.updateLobbyTournamentRankingsState(data);
            return;
        }

        if (data.type === 'home') {
            this.model.app.setState((prevState) => {
                const upcomingTournaments = Array.isArray(data.upcomingTournaments)
                    ? data.upcomingTournaments.map((tournament: any) => this.normalizeHomeLobbyTournament(tournament, false))
                    : [];
                const registeredTournaments = Array.isArray(data.registeredTournaments)
                    ? data.registeredTournaments.map((tournament: any) => this.normalizeHomeLobbyTournament(tournament, true))
                    : [];
                const tournaments = this.mergeHomeLobbyTournaments(registeredTournaments, upcomingTournaments);

                return {
                    lobby: {
                        rooms: Array.isArray(data.rooms) ? data.rooms : [],
                        chatMessages: data.chatMessages || prevState.lobby.chatMessages || [],
                    },
                    lobbyTournament: {
                        chatMessages: prevState.lobbyTournament?.chatMessages || [],
                        tournaments,
                        upcomingTournaments,
                        registeredTournaments
                    },
                } as Pick<IAppState, "lobby" | "lobbyTournament">;
            });
            return;
        }

        const hasTournamentList = Array.isArray(data?.tournaments);

        if (data.type === 'tournament' || hasTournamentList) {
            return;
        }

        // For normal lobby updates - add shallow comparison
        this.model.app.setState((prevState) => {
            const prevRooms = prevState.lobby?.rooms || [];
            const newRooms = data.rooms || [];

            // Skip update if nothing changed
            if (prevRooms.length === newRooms.length && prevRooms.length > 0) {
                const prevMap = new Map(prevRooms.map(r => [r.roomId, r]));
                let hasChanges = false;

                for (const newR of newRooms) {
                    const prevR = prevMap.get(newR.roomId);
                    if (!prevR ||
                        prevR.playerSatCount !== newR.playerSatCount ||
                        prevR.isActive !== newR.isActive) {
                        hasChanges = true;
                        break;
                    }
                }

                if (!hasChanges) return null;
            }

            return {
                lobby: {
                    rooms: newRooms,
                    chatMessages: data.chatMessages || prevState.lobby.chatMessages || [],
                },
            };
        });
    }

    public requestLobbyUpdate = () => {
        const currentSection = this.model.app.state.user.section;
        const lobbyType = this.getLobbyTypeFromSection(currentSection);
        if (lobbyType) {
            if (lobbyType === 'tournament') {
                return;
            }
            this.connection?.send('lobby', 'requestLobbyUpdate', { type: lobbyType });
        } else {
        }
    }

    public addChatMessage = (message: string, user: string) => {
        const currentSection = this.model.app.state.user.section;
        if (currentSection === AppSections.LOBBY_TOURNAMENT) {
            this.model.app.setState((prevState) => ({
                lobbyTournament: {
                    ...prevState.lobbyTournament,
                    chatMessages: [...(prevState.lobbyTournament?.chatMessages || []), { message, user }]
                }
            }) as Pick<IAppState, "lobbyTournament">);
        } else {
            this.model.app.setState((prevState) => ({
                lobby: {
                    ...prevState.lobby,
                    chatMessages: [...(prevState.lobby.chatMessages || []), { message, user }]
                }
            }) as Pick<IAppState, "lobby">);
        }
    }

    public setChatHistory = (data: any) => {
        const currentSection = this.model.app.state.user.section;
        if (currentSection === AppSections.LOBBY_TOURNAMENT) {
            this.model.app.setState((prevState) => ({
                lobbyTournament: {
                    ...prevState.lobbyTournament,
                    chatMessages: [...data.chatMessages]
                }
            }));
        } else {
            this.model.app.setState((prevState) => ({
                lobby: {
                    ...prevState.lobby,
                    chatMessages: [...data.chatMessages]
                }
            }));
        }
    }

    public removeJoinedRoom = (roomId: string) => {
        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.filter((room) => room.roomId !== roomId)
        }));
    }

    public isUserInSection = (section: string): boolean => {
        return this.model.app.state.user.section === section;
    }

    public resetState = () => {
        this.model.resetState();
    }

    public addRoomActivityMessage = (data: {
        roomId: string;
        message: {
            template: MessageType;
            data: ActivityMessageData;
            messageKey?: string;
            params?: Record<string, unknown>;
        }
    }): void => {
        const { roomId, message } = data;

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) =>
                room.roomId === roomId
                    ? {
                        ...room,
                        activityMessages: [
                            ...room.activityMessages,
                            {
                                template: message.template,
                                data: {
                                    ...message.data,
                                    timestamp: message?.data?.timestamp || new Date().toISOString()
                                },
                                messageKey: message.messageKey,
                                params: message.params
                            }
                        ]
                    }
                    : room
            )
        }));
    };

    public handleRoomJoinSuccessful = (data: any) => {
        const snapshot = this.getProtocolSnapshot(data, 'state');
        if (!snapshot) return;

        const roomId = snapshot.roomId;
        let roomState = roomId ? this.model.app.state.lobby.rooms.find((room) => room.roomId === roomId) : null;

        // Tournament rooms can still show up with type 'texas' in normal lobby lists.
        // Always try to enrich joinSuccessful with tournament metadata when the roomId belongs to an active tournament.
        let tournamentContext: { tournament: any; tournamentRoom: any } | null = null;
        if (roomId) {
            for (const tournament of this.model.app.state.lobbyTournament.tournaments) {
                const active = tournament?.activeRooms;
                if (!Array.isArray(active)) continue;
                const tournamentRoom = active.find((r: any) => r?.roomId === roomId);
                if (tournamentRoom) {
                    tournamentContext = { tournament, tournamentRoom };
                    break;
                }
            }
        }

        if (tournamentContext && roomId) {
            const { tournament, tournamentRoom } = tournamentContext;
            roomState = {
                ...(roomState || ({} as any)),
                roomId: tournamentRoom.roomId,
                name:
                    (roomState as any)?.name ||
                    tournamentRoom.tableName ||
                    tournamentRoom.roomName ||
                    data?.name ||
                    `Tournament Table ${roomId}`,
                gameType: tournament.type,
                type: tournament.type,
                minPlayers: (roomState as any)?.minPlayers ?? 2,
                maxPlayers: tournamentRoom.maximumPlayers || (roomState as any)?.maxPlayers || 9,
                minBuyIn: (roomState as any)?.minBuyIn ?? 0,
                maxBuyIn: (roomState as any)?.maxBuyIn ?? 0,
                smallBlind: (roomState as any)?.smallBlind ?? 0,
                bigBlind: (roomState as any)?.bigBlind ?? 0,
                isActive: true,
                players: (roomState as any)?.players ?? [],
                playerSatCount: tournamentRoom.playerCount ?? (roomState as any)?.playerSatCount ?? 0,
                ante: (roomState as any)?.ante ?? 0,
                currency: tournament.currency || (roomState as any)?.currency || 'USD',
                currencyType: tournament.currencyType || (roomState as any)?.currencyType || 'fiat',
                exchangeCurrencyValue: (roomState as any)?.exchangeCurrencyValue ?? 1,
                stakeLevel: (roomState as any)?.stakeLevel ?? '',
                tournamentId: tournament.tournamentId || tournament.gameId,
                tournamentGameId: tournament.gameId,
                isTournamentTable: true,
                isFinalTournamentTable: tournamentRoom.isFinalTournamentTable || (roomState as any)?.isFinalTournamentTable || false,
                isTableClosed: (roomState as any)?.isTableClosed ?? false,
            } as IRoomState;
        }

        if (roomState || snapshot.state) {
            const serverState = {
                ...snapshot.state,
                roomId: snapshot.roomId,
                gameId: snapshot.gameId,
                handId: snapshot.handId,
                stateVersion: snapshot.stateVersion,
                lastAppliedGameplaySequence: snapshot.lastAppliedGameplaySequence,
                serverTime: snapshot.serverTime
            };
            this.seedProtocolCacheFromSnapshot(snapshot, serverState);
            const isTournamentTable =
                Boolean(data?.isTournament) ||
                Boolean(data?.tournamentGameId) ||
                Boolean(data?.tournamentId) ||
                Boolean(snapshot.state?.tournamentGameId) ||
                Boolean(snapshot.state?.tournamentId) ||
                Boolean((roomState as any)?.isTournamentTable) ||
                Boolean((roomState as any)?.tournamentGameId) ||
                Boolean((roomState as any)?.tournamentId);

            const normalizedType = (() => {
                const raw = data?.type || (roomState as any)?.type || (roomState as any)?.gameType || 'texas';
                if (raw === 'tournament') {
                    return (roomState as any)?.gameType || 'texas';
                }
                return raw;
            })();

            const joinedRoomState = JoinedRoomStateFactory.createFromRoomJoin({
                roomState: (roomState as any) || null,
                serverState,
                type: normalizedType,
                gameId: snapshot.gameId,
                tournamentGameId: snapshot.state?.tournamentGameId || data.tournamentGameId || (roomState as any)?.tournamentGameId,
                name: data.name,
                roomId: snapshot.roomId,
                isTournament: isTournamentTable,
            });

            this.addOrUpdateJoinedRoom(joinedRoomState);
            if (!isMobile && hasReservedWindow(snapshot.roomId)) {
                return;
            }

            const currentSection = this.model.app.state.user.section;
            const currentLobbyType = this.getLobbyTypeFromSection(currentSection);
            if (currentLobbyType) {
                this.connection?.leaveLobby(currentLobbyType);
            }
            this.setSingleViewRoomId(snapshot.roomId, () => {
                this.setSection(AppSections.POKER_SINGLE_ROOM_VIEW);
            });
        } else {
            console.error('Room not found');
        }
    }

    public addOrUpdateJoinedRoom = (data: IJoinedRoomState) => {
        this.model.app.setState((prevState) => {
            const roomExists = prevState.joinedRooms.some((room) => room.roomId === data.roomId);
            const updatedRooms = roomExists
                ? prevState.joinedRooms.map((room) => room.roomId === data.roomId ? { ...room, ...data } : room)
                : [...prevState.joinedRooms, data];
            return { joinedRooms: updatedRooms };
        });
    }

    private joinInClientLastSeen: Map<string, number> = new Map();

    public addJoinedRoom = (data: IJoinedRoomState) => {
        const newJoinedRoom = JoinedRoomStateFactory.createWithDefaults(data);
        this.model.app.setState((prevState) => ({
            joinedRooms: [...prevState.joinedRooms, newJoinedRoom]
        }));
    }

    public joinRoomFromServerRequest = (data: any) => {
        const snapshot = this.getProtocolSnapshot(data, 'roomState');
        if (!snapshot) return;

        const newRoomState = {
            ...snapshot.state,
            roomId: snapshot.roomId,
            gameId: snapshot.gameId,
            handId: snapshot.handId,
            stateVersion: snapshot.stateVersion,
            lastAppliedGameplaySequence: snapshot.lastAppliedGameplaySequence,
            serverTime: snapshot.serverTime,
            tournamentId: snapshot.state?.tournamentId || data?.tournamentId,
            tournamentGameId: snapshot.state?.tournamentGameId || data?.tournamentGameId,
            newRoomId: data?.newRoomId,
            tableAssignment: data?.tableAssignment
        };
        this.seedProtocolCacheFromSnapshot(snapshot, newRoomState);

        const roomId: string = snapshot.roomId;
        if (isPopupDebugEnabled()) {
            console.debug('[joinInClient] Received', {
                roomId,
                isTournamentTable: newRoomState?.isTournamentTable,
                tournamentGameId: newRoomState?.tournamentGameId,
                tournamentId: newRoomState?.tournamentId,
                gameId: newRoomState?.gameId
            });
        }
        // If the room is already joined, just sync state and return
        if (this.model.app.state.joinedRooms.some(room => room.roomId === roomId)) {
            const preparedSync = JoinedRoomStateFactory.createFromServerRequest(newRoomState);
            this.addOrUpdateJoinedRoom(preparedSync);
            return;
        }

        // De-dup within 2 seconds: sync only and return
        const lastSeen = this.joinInClientLastSeen.get(roomId) || 0;
        const now = Date.now();
        if (now - lastSeen < 2000) {
            const preparedSync = JoinedRoomStateFactory.createFromServerRequest(newRoomState);
            this.addOrUpdateJoinedRoom(preparedSync);
            return;
        }
        this.joinInClientLastSeen.set(roomId, now);

        const isCurrentlyInTournamentTable = this.isPlayerInTournamentTable();
        let isNewRoomTournamentTable = newRoomState?.isTournamentTable ||
            Boolean(newRoomState?.tournamentGameId) ||
            Boolean(newRoomState?.tournamentId);

        let enrichedRoomState = newRoomState;

        const isSameTournamentMove = this.isSameTournamentTableMove(enrichedRoomState);
        if (isPopupDebugEnabled()) {
            console.debug('[joinInClient] Routing', {
                roomId,
                isCurrentlyInTournamentTable,
                isNewRoomTournamentTable,
                isSameTournamentMove
            });
        }

        // If we are already focused on this room, just sync state
        const activeRoomId = this.model.app.state.user.singleViewRoomId;
        if (activeRoomId && activeRoomId === roomId) {
            const preparedSync = JoinedRoomStateFactory.createFromServerRequest(enrichedRoomState);
            this.addOrUpdateJoinedRoom(preparedSync);
            return;
        }

        if (isCurrentlyInTournamentTable && isNewRoomTournamentTable && isSameTournamentMove) {
            this.handleTournamentTableMove(enrichedRoomState);
        } else {
            // This handles: first tournament joins, multiple tournament joins, and regular room joins
            this.handleFirstRoomJoin(enrichedRoomState, isNewRoomTournamentTable);
        }
    }

    private isSameTournamentTableMove = (newRoomState: any): boolean => {
        // Get the new tournament ID
        const newTournamentId = newRoomState.tournamentGameId ||
            newRoomState.tournamentId;

        if (!newTournamentId) {
            return false;
        }

        const existingTournamentRoom = this.model.app.state.joinedRooms.find(room => {
            if (!room.isTournamentTable) return false;
            
            const roomTournamentId = room.tournamentGameId ||
                room.tournamentId;
            
            return roomTournamentId === newTournamentId;
        });

        if (existingTournamentRoom) {
            return true;
        }

        return false;
    }

    private handleFirstRoomJoin = (roomState: any, isTournamentTable: boolean) => {
        try {
            const preparedRoom = JoinedRoomStateFactory.createFromServerRequest(roomState);

            // Validate the prepared room has essential fields
            if (!preparedRoom.roomId || !preparedRoom.gameId) {
                console.error('[joinInClient] Invalid room state after preparation:', preparedRoom);
                return;
            }

            const alreadyJoined = this.model.app.state.joinedRooms.some(room => room.roomId === preparedRoom.roomId);
            if (alreadyJoined) {
                this.addOrUpdateJoinedRoom(preparedRoom);
            } else {
                this.addJoinedRoom(preparedRoom);
            }

            const shouldNavigateMainView = !isTournamentTable || isMobile;
            if (shouldNavigateMainView) {
                const currentSection = this.model.app.state.user.section;
                const currentLobbyType = this.getLobbyTypeFromSection(currentSection);
                if (currentLobbyType) {
                    this.connection?.leaveLobby(currentLobbyType);
                }
                this.setSingleViewRoomId(preparedRoom.roomId, () => {
                    this.setSection(AppSections.POKER_SINGLE_ROOM_VIEW);
                });
            }

            // Show appropriate message for tournament joins
            if (isTournamentTable) {
                // Check if this is an additional tournament table
                const existingTournamentRooms = this.model.app.state.joinedRooms.filter(
                    room => room.isTournamentTable && room.roomId !== preparedRoom.roomId
                );

                const message = existingTournamentRooms.length > 0
                    ? t('Notifications:tournament.joinedAdditionalTable', { count: existingTournamentRooms.length + 1 })
                    : t('Notifications:tournament.joinedTable');

                this.updateRoomState({
                    roomId: preparedRoom.roomId,
                    state: {
                        pendingNotification: {
                            message: message,
                            type: 'SYSTEM_SUCCESS',
                            timestamp: Date.now()
                        }
                    }
                });
            }
        } catch (error) {
            console.error('[joinInClient] Error in handleFirstRoomJoin:', error);
            throw error;
        }
    }

    private isPlayerInTournamentTable = (): boolean => {
        const currentSection = this.model.app.state.user.section;
        const currentRoomId = this.model.app.state.user.singleViewRoomId;

        // Check if user is currently viewing a room
        if (currentSection !== AppSections.POKER_SINGLE_ROOM_VIEW || !currentRoomId) {
            const hasActiveTournamentRoom = this.model.app.state.joinedRooms.some(
                room => room.isTournamentTable === true
            );

            if (hasActiveTournamentRoom) {
                return true;
            }

            return false;
        }

        const currentRoom = this.model.app.state.joinedRooms.find(
            room => room.roomId === currentRoomId
        );

        const isInTournament = currentRoom?.isTournamentTable === true;

        return isInTournament;
    }

    private handleTournamentTableMove = (newRoomState: IJoinedRoomState) => {
        // Find the existing tournament room with the same tournament ID
        const newTournamentId = newRoomState.tournamentGameId ||
            newRoomState.tournamentId;

        if (!newTournamentId) {
            console.error('[Tournament Move] Missing tournament identity on joinInClient payload:', newRoomState);
            this.handleFirstRoomJoin(newRoomState, true);
            return;
        }

        const currentRoom = this.model.app.state.joinedRooms.find(room => {
            if (!room.isTournamentTable) return false;
            const roomTournamentId = room.tournamentGameId || room.tournamentId;
            return roomTournamentId === newTournamentId;
        });

        if (!currentRoom) {
            this.handleFirstRoomJoin(newRoomState, true);
            return;
        }

        const currentRoomId = currentRoom.roomId;

        try {
            const preparedNewRoom = JoinedRoomStateFactory.createFromServerRequest(newRoomState);

            // Validate the prepared room
            if (!preparedNewRoom.roomId || !preparedNewRoom.gameId) {
                console.error('[Tournament Move] Invalid new room state:', preparedNewRoom);
                return;
            }

            // Transfer the detached window and state to the new room
            transferDetachedWindow(currentRoomId, preparedNewRoom.roomId);
            // Keep focus registry in sync immediately (avoids false "focus_failed" during move timing).
            windowFocusManager.transferWindow(currentRoomId, preparedNewRoom.roomId);
            if (isPopupDebugEnabled()) {
                console.debug('[Tournament Move] Transferred detached window', {
                    fromRoomId: currentRoomId,
                    toRoomId: preparedNewRoom.roomId,
                    tournamentId: newTournamentId
                });
            }
            
            // Notify App to transfer detached state BEFORE removing/adding rooms
            if (this.onTournamentRoomTransfer) {
                this.onTournamentRoomTransfer(currentRoomId, preparedNewRoom.roomId);
            }

            const shouldNavigateMainView = isMobile || this.model.app.state.user.section === AppSections.POKER_SINGLE_ROOM_VIEW;
            // Batch remove old room, add new room, and update singleViewRoomId in a single setState
            this.model.app.setState((prevState) => {
                const newJoinedRooms = JoinedRoomStateFactory.createWithDefaults(preparedNewRoom);
                
                return {
                    joinedRooms: prevState.joinedRooms
                        .filter(room => room.roomId !== currentRoomId)  // Remove old room
                        .concat([newJoinedRooms]),                      // Add new room
                    user: {
                        ...prevState.user,
                        singleViewRoomId: shouldNavigateMainView ? newJoinedRooms.roomId : prevState.user.singleViewRoomId
                    }
                };
            });
            
            // Add notification AFTER a short delay to ensure DetachedRoom is mounted
            setTimeout(() => {
                this.updateRoomState({
                    roomId: preparedNewRoom.roomId,
                    state: {
                        pendingNotification: {
                            message: t('Notifications:tournament.movedToNewTable'),
                            type: 'TOURNAMENT_INFO',
                            timestamp: Date.now()
                        }
                    }
                });
            }, 200);

            if (shouldNavigateMainView) {
                const currentSection = this.model.app.state.user.section;
                const currentLobbyType = this.getLobbyTypeFromSection(currentSection);
                if (currentLobbyType) {
                    this.connection?.leaveLobby(currentLobbyType);
                }

                // Ensure we're in table view
                if (this.model.app.state.user.section !== AppSections.POKER_SINGLE_ROOM_VIEW) {
                    this.setSection(AppSections.POKER_SINGLE_ROOM_VIEW);
                }
            }
        } catch (error) {
            console.error('[Tournament Move] Error in handleTournamentTableMove:', error);
            throw error;
        }
    }

    public getRoomState = (roomId: string) => {
        return this.model.app.state.joinedRooms.find((room) => room.roomId === roomId);
    }

    public handleRoomStateUpdate = (data: any) => {
        const snapshot = this.getProtocolSnapshot(data, 'state');
        if (!snapshot) return;

        const cache = this.getProtocolCache(snapshot.roomId);
        const sameHand = Boolean(cache && cache.gameId === snapshot.gameId && cache.handId === snapshot.handId);
        if (cache && sameHand && snapshot.stateVersion <= cache.stateVersion) {
            return;
        }

        const stateWithMetadata = {
            ...snapshot.state,
            roomId: snapshot.roomId,
            gameId: snapshot.gameId,
            handId: snapshot.handId,
            stateVersion: snapshot.stateVersion,
            lastAppliedGameplaySequence: snapshot.lastAppliedGameplaySequence,
            serverTime: snapshot.serverTime
        };

        const players = Array.isArray(stateWithMetadata.players) ? stateWithMetadata.players : [];
        if (cache && sameHand) {
            cache.stateVersion = snapshot.stateVersion;
            cache.lastAppliedGameplaySequence = snapshot.lastAppliedGameplaySequence;
            cache.playerIds = new Set(players.map((player: any) => this.getProtocolString(player?.userId)).filter(Boolean) as string[]);
        } else {
            this.seedProtocolCacheFromSnapshot(snapshot, stateWithMetadata);
        }

        const protocolCache = this.getProtocolCache(snapshot.roomId);

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) => {
                if (room.roomId !== snapshot.roomId) return room;

                const isNewHand = room.gameId !== snapshot.gameId || room.handId !== snapshot.handId;
                const hasActivePlayerId = Object.prototype.hasOwnProperty.call(stateWithMetadata, 'activePlayerId');
                const activePlayerChanged = hasActivePlayerId && stateWithMetadata.activePlayerId !== room.activePlayerId;
                const shouldClearPrivateTurnCommand = isNewHand || activePlayerChanged;
                const nextRoundName = this.getProtocolString(stateWithMetadata.roundName);
                const shouldClearAutoActionIntent = isNewHand || Boolean(
                    room.autoActionRoundName &&
                    nextRoundName &&
                    room.autoActionRoundName !== nextRoundName
                );
                const nextServerState: any = { ...stateWithMetadata };
                delete nextServerState.availableActions;

                return {
                    ...room,
                    ...nextServerState,
                    availableActions: shouldClearPrivateTurnCommand ? null : room.availableActions,
                    turnId: shouldClearPrivateTurnCommand ? undefined : room.turnId,
                    actionRequestId: shouldClearPrivateTurnCommand ? undefined : room.actionRequestId,
                    lastWinnerDeclaredEvent: isNewHand ? undefined : room.lastWinnerDeclaredEvent,
                    lastWinnerDeclaredReceivedAt: isNewHand ? undefined : room.lastWinnerDeclaredReceivedAt,
                    autoActionIntent: shouldClearAutoActionIntent ? null : room.autoActionIntent,
                    autoActionRoundName: shouldClearAutoActionIntent ? null : room.autoActionRoundName,
                    lastGameplayEventSequence: protocolCache?.lastGameplayEventSequence ?? snapshot.lastAppliedGameplaySequence
                };
            })
        }));
    }

    public handleGameplayEvent = (data: IGameplayEvent) => {
        const roomId = this.getProtocolString(data?.roomId);
        const eventId = this.getProtocolString(data?.eventId);
        const sequence = this.getProtocolNumber(data?.sequence);
        const gameId = this.getProtocolString(data?.gameId);
        const handId = this.getProtocolString(data?.handId);
        const serverTime = this.getProtocolString(data?.serverTime);
        const kind = this.getProtocolString(data?.kind);

        if (!roomId || !eventId || sequence === null || !gameId || !handId || !serverTime || !kind) {
            this.requestRoomStateForProtocolError(roomId, 'Invalid gameplayEvent metadata', data);
            return;
        }

        if (!isSupportedGameplayEventKind(kind)) {
            this.requestRoomStateForProtocolError(roomId, 'Unsupported gameplayEvent kind', data);
            return;
        }

        const actorUserId = this.getProtocolString(data.actorUserId);
        const action = this.getProtocolString(data.action);
        let protocolActorUserId: string | undefined;
        let protocolAction: string | undefined;
        if (kind === 'playerAction' && (!actorUserId || !action)) {
            this.requestRoomStateForProtocolError(roomId, 'Invalid playerAction gameplayEvent payload', data);
            return;
        }
        if (kind === 'playerAction' && actorUserId && action) {
            protocolActorUserId = actorUserId;
            protocolAction = action;
        }

        const street = this.getProtocolString(data.street);
        let protocolStreet: IGameplayEvent['street'];
        let protocolStreetCards: string[] | undefined;
        let protocolStreetCardsCount: number | undefined;
        let protocolStreetCommunityCardsCount: number | undefined;
        if (kind === 'streetDealt') {
            if (!isSupportedGameplayEventStreet(street)) {
                this.requestRoomStateForProtocolError(roomId, 'Invalid streetDealt gameplayEvent payload', data);
                return;
            }

            protocolStreet = street;
            const expectedPayload = STREET_DEALT_PAYLOAD_REQUIREMENTS[protocolStreet];
            const streetCards: string[] = [];
            if (Array.isArray(data.cards)) {
                data.cards.forEach((card) => {
                    const protocolCard = this.getProtocolString(card);
                    if (protocolCard) streetCards.push(protocolCard);
                });
            }
            const cardsCount = this.getProtocolNumber(data.cardsCount);
            const communityCardsCount = this.getProtocolNumber(data.communityCardsCount);

            if (
                cardsCount === null ||
                communityCardsCount === null ||
                streetCards.length !== expectedPayload.cardsCount ||
                cardsCount !== expectedPayload.cardsCount ||
                communityCardsCount !== expectedPayload.communityCardsCount
            ) {
                this.requestRoomStateForProtocolError(roomId, 'Invalid streetDealt gameplayEvent card payload', data);
                return;
            }

            protocolStreetCards = streetCards;
            protocolStreetCardsCount = cardsCount;
            protocolStreetCommunityCardsCount = communityCardsCount;
        }

        if (kind === 'winnerDeclared') {
            const winners = Array.isArray(data.winners) ? data.winners : [];
            const winnerIds = Array.isArray(data.winnerIds) ? data.winnerIds : [];
            const hasValidWinners = winners.length > 0 && winners.every((winner) => Boolean(this.getProtocolString(winner?.userId)));
            const hasValidWinnerIds = winnerIds.length > 0 && winnerIds.every((winnerId) => Boolean(this.getProtocolString(winnerId)));
            if (!hasValidWinners && !hasValidWinnerIds) {
                this.requestRoomStateForProtocolError(roomId, 'Invalid winnerDeclared gameplayEvent payload', data);
                return;
            }
        }

        const cache = this.getProtocolCache(roomId);
        if (!cache) {
            this.requestRoomStateForProtocolError(roomId, 'gameplayEvent received before room snapshot', data);
            return;
        }

        if (cache.gameId !== gameId || cache.handId !== handId) {
            this.requestRoomStateForProtocolError(roomId, 'gameplayEvent hand/game mismatch', data);
            return;
        }

        if (kind === 'playerAction' && protocolActorUserId && !cache.playerIds.has(protocolActorUserId)) {
            this.requestRoomStateForProtocolError(roomId, 'playerAction actorUserId not found in current players', data);
            return;
        }

        if (cache.seenGameplayEventIds.includes(eventId)) {
            return;
        }

        if (sequence <= cache.lastGameplayEventSequence) {
            this.requestRoomStateForProtocolError(roomId, 'Stale gameplayEvent sequence', data);
            return;
        }

        if (sequence !== cache.lastGameplayEventSequence + 1) {
            this.requestRoomStateForProtocolError(roomId, 'Non-contiguous gameplayEvent sequence', data);
            return;
        }

        this.rememberProtocolGameplayEvent(cache, eventId, sequence);

        const {
            street: _receivedStreet,
            cards: _receivedCards,
            cardsCount: _receivedCardsCount,
            communityCardsCount: _receivedCommunityCardsCount,
            actorUserId: _receivedActorUserId,
            action: _receivedAction,
            ...eventData
        } = data;
        const nextEvent: IGameplayEvent = {
            ...eventData,
            eventId,
            roomId,
            gameId,
            handId,
            sequence,
            kind,
            serverTime,
            ...(protocolActorUserId ? { actorUserId: protocolActorUserId } : {}),
            ...(protocolAction ? { action: protocolAction } : {}),
            ...(protocolStreet ? {
                street: protocolStreet,
                cards: protocolStreetCards,
                cardsCount: protocolStreetCardsCount,
                communityCardsCount: protocolStreetCommunityCardsCount
            } : {})
        };

        roomGameplayEventBus.publish(nextEvent);

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) => {
                if (room.roomId !== roomId) return room;

                return {
                    ...room,
                    lastGameplayEventSequence: sequence,
                    lastGameplayEventId: eventId,
                    lastWinnerDeclaredEvent: kind === 'winnerDeclared' ? nextEvent : room.lastWinnerDeclaredEvent,
                    lastWinnerDeclaredReceivedAt: kind === 'winnerDeclared' ? Date.now() : room.lastWinnerDeclaredReceivedAt
                };
            })
        }));
    }

    public handleTableReaction = (data: any) => {
        const reactionEventId = this.getProtocolString(data?.reactionEventId);
        const roomId = this.getProtocolString(data?.roomId);
        const sequence = this.getProtocolNumber(data?.sequence);
        const serverTime = this.getProtocolString(data?.serverTime);
        const reactionId = this.getProtocolString(data?.reactionId);
        const sourceUserId = this.getProtocolString(data?.sourceUserId);
        const sourceUsername = this.getProtocolString(data?.sourceUsername);
        const targetUserId = this.getProtocolString(data?.targetUserId);
        const targetUsername = this.getProtocolString(data?.targetUsername);

        if (
            !reactionEventId ||
            !roomId ||
            sequence === null ||
            !serverTime ||
            !isSupportedTableReactionId(reactionId) ||
            !sourceUserId ||
            !sourceUsername ||
            !targetUserId ||
            !targetUsername
        ) {
            console.warn('[room-social] Invalid tableReaction payload', data);
            return;
        }

        const sourceAvatarUrl = this.getProtocolString(data?.sourceAvatarUrl);
        const targetAvatarUrl = this.getProtocolString(data?.targetAvatarUrl);
        const sourceSeatIndex = this.getProtocolNumber(data?.sourceSeatIndex);
        const targetSeatIndex = this.getProtocolNumber(data?.targetSeatIndex);

        const event: ITableReactionEvent = {
            reactionEventId,
            roomId,
            sequence,
            serverTime,
            reactionId,
            sourceUserId,
            sourceUsername,
            ...(sourceAvatarUrl ? { sourceAvatarUrl } : {}),
            ...(sourceSeatIndex !== null ? { sourceSeatIndex } : {}),
            targetUserId,
            targetUsername,
            ...(targetAvatarUrl ? { targetAvatarUrl } : {}),
            ...(targetSeatIndex !== null ? { targetSeatIndex } : {})
        };

        roomTableReactionEventBus.publish(event);
    }

    public handleTableReactionRejected = (data: any) => {
        const roomId = this.getProtocolString(data?.roomId);
        const reason = this.getProtocolString(data?.reason);
        if (!roomId || !isSupportedTableReactionRejectedReason(reason)) {
            console.warn('[room-social] Invalid tableReactionRejected payload', data);
            return;
        }

        const retryAfterMs = this.getProtocolNumber(data?.retryAfterMs);
        const rejection: ITableReactionRejected = {
            roomId,
            reason,
            ...(retryAfterMs !== null ? { retryAfterMs } : {})
        };
        if (rejection.reason === 'rate_limited') {
            this.showNotification('Please wait before sending another reaction.', {
                variant: 'info',
                autoHideDuration: 1800
            });
        } else if (rejection.reason === 'source_not_active') {
            this.showNotification('You need to be active in the hand to send a throwable.', {
                variant: 'info',
                autoHideDuration: 2200
            });
        }
    }

    public handleSitOnRoomSuccessful = (data: any) => {
        this.model.app.setState((prevState) => {
            const updatedRooms = prevState.joinedRooms.map((room) => {
                if (room.roomId === data.roomId) {
                    return {
                        ...room,
                        showSitOptionsModal: false,
                        sitOptions: null,
                    };
                }
                return room;
            });

            return { joinedRooms: updatedRooms };
        });
    };

    public handleSitOnRoomFailedNoMoney = (data: any) => {
        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) =>
                room.roomId === data.roomId
                    ? { ...room, showSitFailedNoMoneyAlert: true }
                    : room
            )
        }));
    }

    public updateRoomState = (data: { roomId: string, state: Partial<IJoinedRoomState> }) => {
        // disable availableActions from being updated by the generic stateUpdate
        const sanitizedState: Partial<IJoinedRoomState> = { ...data.state };
        if (sanitizedState && Object.prototype.hasOwnProperty.call(sanitizedState, 'availableActions')) {
            delete (sanitizedState as any).availableActions;
        }

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) => {
                if (room.roomId !== data.roomId) return room;
                const nextState: any = { ...room, ...sanitizedState };
                const incomingStateName = (sanitizedState as any)?.stateName;
                const incomingGameId = (sanitizedState as any)?.gameId;
                const isTexas = (room.type === 'texas') || ((sanitizedState as any)?.type === 'texas');
                if (isTexas && ((incomingStateName && incomingStateName !== room.stateName) || (incomingGameId && incomingGameId !== room.gameId))) {
                    nextState.availableActions = null;
                }

                return nextState;
            })
        }));
    }

    public requestRoomState = (roomId: string) => {
        this.connection?.requestRoomState(roomId);
    }

    public handlePlayerTurnRequest = (data: any) => {
        const roomId = this.getProtocolString(data?.roomId);
        const gameId = this.getProtocolString(data?.gameId);
        const handId = this.getProtocolString(data?.handId);
        const turnId = this.getProtocolString(data?.turnId);
        const actionRequestId = this.getProtocolString(data?.actionRequestId);
        const availableActions = Array.isArray(data?.availableActions) ? data.availableActions : null;

        if (!roomId || !gameId || !handId || !turnId || !actionRequestId || !availableActions) {
            this.requestRoomStateForProtocolError(roomId, 'Invalid playerTurn payload', data);
            return;
        }

        const cache = this.getProtocolCache(roomId);
        if (!cache || cache.gameId !== gameId || cache.handId !== handId) {
            this.requestRoomStateForProtocolError(roomId, 'playerTurn hand/game mismatch', data);
            return;
        }

        const playerId = this.model.app.state.user.id;
        const formattedAvailableActions = {
            userId: playerId,
            gameId,
            handId,
            turnId,
            actionRequestId,
            actions: availableActions.map((action: any) => ({
                type: action.type,
                minAmount: action.minAmount,
                maxAmount: action.maxAmount,
                timer: action.timer || 10000
            }))
        };

        this.model.app.setState((prevState) => {
            const newState = {
                joinedRooms: prevState.joinedRooms.map((room) =>
                    room.roomId === roomId
                        ? {
                            ...room,
                            players: room.players.map((player) =>
                                player.userId === playerId
                                    ? { ...player, availableActions: formattedAvailableActions.actions, potentialActions: formattedAvailableActions.actions }
                                    : player
                            ),
                            turnId,
                            actionRequestId,
                            availableActions: formattedAvailableActions
                        }
                        : room
                )
            };

            return newState;
        });
    };

    public handleRejoinFailed = (data: any) => {
        const message = data.message
            ? t('Notifications:room.rejoinFailed', { message: data.message })
            : t('Notifications:room.rejoinFailedUnknown');
        alert(message);
    };

    public sendBalanceUpdate = (roomId: string, userId: string, amount: number) => {
        this.connection?.send('room', 'balanceUpdate', { roomId, userId, amount }, roomId);
    }

    public requestSetAutoAction = (roomId: string, intent: AutoActionIntent, maxCallAmount?: number) => {
        const room = this.model.app.state.joinedRooms.find((joinedRoom) => joinedRoom.roomId === roomId);
        const roundName = room?.roundName || room?.stateName;
        if (!room?.gameId || !room?.handId || !roundName) {
            this.requestRoomStateForProtocolError(roomId, 'setAutoAction attempted without room hand metadata', { roomId, intent });
            return;
        }

        if (intent === 'call' && typeof maxCallAmount !== 'number') {
            this.requestRoomStateForProtocolError(roomId, 'setAutoAction call intent attempted without maxCallAmount', { roomId, intent });
            return;
        }

        const data: {
            roomId: string;
            gameId: string;
            handId: string;
            roundName: string;
            intent: AutoActionIntent;
            maxCallAmount?: number;
        } = {
            roomId,
            gameId: room.gameId,
            handId: room.handId,
            roundName,
            intent
        };

        if (intent === 'call') {
            data.maxCallAmount = maxCallAmount;
        }

        this.connection?.send('room', 'setAutoAction', data, roomId);
    }

    public requestSendTableReaction = (roomId: string, targetUserId: string, reactionId: TableReactionId) => {
        if (!roomId || !targetUserId || !isSupportedTableReactionId(reactionId)) {
            console.warn('[room-social] sendTableReaction attempted with invalid payload', { roomId, targetUserId, reactionId });
            return;
        }

        this.connection?.send('room', 'sendTableReaction', {
            roomId,
            targetUserId,
            reactionId
        }, roomId);
    }

    public requestCancelAutoAction = (roomId: string) => {
        const room = this.model.app.state.joinedRooms.find((joinedRoom) => joinedRoom.roomId === roomId);
        const roundName = room?.roundName || room?.stateName;
        if (!room?.gameId || !room?.handId || !roundName) {
            this.requestRoomStateForProtocolError(roomId, 'cancelAutoAction attempted without room hand metadata', { roomId });
            return;
        }

        this.connection?.send('room', 'cancelAutoAction', {
            roomId,
            gameId: room.gameId,
            handId: room.handId,
            roundName
        }, roomId);
    }

    public handleAutoActionUpdated = (data: any) => {
        const roomId = this.getProtocolString(data?.roomId);
        const gameId = this.getProtocolString(data?.gameId);
        const handId = this.getProtocolString(data?.handId);
        const roundName = this.getProtocolString(data?.roundName);
        const intent = this.getProtocolString(data?.intent);

        if (!roomId || !gameId || !handId || !roundName || !this.isSupportedAutoActionIntent(intent)) {
            this.requestRoomStateForProtocolError(roomId, 'Invalid autoActionUpdated payload', data);
            return;
        }

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) => (
                this.isAutoActionAckForRoom(room, roomId, gameId, handId, roundName)
                    ? { ...room, autoActionIntent: intent, autoActionRoundName: roundName }
                    : room
            ))
        }));
    }

    public handleAutoActionCancelled = (data: any) => {
        const roomId = this.getProtocolString(data?.roomId);
        const gameId = this.getProtocolString(data?.gameId);
        const handId = this.getProtocolString(data?.handId);
        const roundName = this.getProtocolString(data?.roundName);

        if (!roomId || !gameId || !handId || !roundName) {
            this.requestRoomStateForProtocolError(roomId, 'Invalid autoActionCancelled payload', data);
            return;
        }

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) => (
                this.isAutoActionAckForRoom(room, roomId, gameId, handId, roundName)
                    ? { ...room, autoActionIntent: null, autoActionRoundName: roundName }
                    : room
            ))
        }));
    }

    public handleAutoActionRejected = (data: any) => {
        const roomId = this.getProtocolString(data?.roomId);
        const gameId = this.getProtocolString(data?.gameId);
        const handId = this.getProtocolString(data?.handId);
        const roundName = this.getProtocolString(data?.roundName);

        if (!roomId || !gameId || !handId || !roundName) {
            console.warn('[room-protocol-v2] Invalid autoActionRejected payload', data);
            return;
        }

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) => (
                this.isAutoActionAckForRoom(room, roomId, gameId, handId, roundName)
                    ? { ...room, autoActionIntent: null, autoActionRoundName: roundName }
                    : room
            ))
        }));
    }

    private isAutoActionAckForRoom = (
        room: IJoinedRoomState,
        roomId: string,
        gameId: string,
        handId: string,
        roundName: string
    ): boolean => {
        const roomRoundName = room.roundName || room.stateName;
        return room.roomId === roomId &&
            room.gameId === gameId &&
            room.handId === handId &&
            roomRoundName === roundName;
    }

    public sendAction = (roomId: string, action: string, amount: number, userId: string) => {
        if (action === 'rejoinNextHand') {
            this.connection?.send('room', 'rejoinNextHand', { roomId, userId }, roomId);
            return;
        }

        const room = this.model.app.state.joinedRooms.find((joinedRoom) => joinedRoom.roomId === roomId);
        const availableActions = room?.availableActions?.actions || [];
        const requestedAction = availableActions.find((availableAction) => availableAction.type === action);
        if (!room || !requestedAction || !room.gameId || !room.handId || !room.turnId || !room.actionRequestId) {
            this.requestRoomStateForProtocolError(roomId, 'playerTurnResponse attempted without matching playerTurn command', { roomId, action, amount });
            return;
        }

        this.connection?.send('room', 'playerTurnResponse', {
            roomId,
            gameId: room.gameId,
            handId: room.handId,
            turnId: room.turnId,
            actionRequestId: room.actionRequestId,
            action,
            amount,
            userId
        }, roomId);
    }

    private isSupportedAutoActionIntent = (intent: string | null): intent is AutoActionIntent => {
        return intent === 'checkOrFold' ||
            intent === 'fold' ||
            intent === 'call' ||
            intent === 'callAny' ||
            intent === 'allIn';
    }

    public requestSitOptions = (roomId: string, seatIndex: number) => {
        this.connection?.send('room', 'requestSitOptions', {
            roomId,
            seatIndex
        }, roomId);
    };

    public handleSitOptionsResponse = (data: any) => {
        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) => {
                if (room.roomId === data.roomId) {
                    return {
                        ...room,
                        sitOptions: {
                            seatIndex: data.seatIndex,
                            buyInLimits: {
                                min: data.buyInLimits.minBuyIn ?? data.buyInLimits.min,
                                max: data.buyInLimits.maxBuyIn ?? data.buyInLimits.max
                            },
                            gameState: {
                                isGameActive: Boolean(data.gameState?.isGameActive),
                                requiresChoice: Boolean(data.gameState?.requiresChoice),
                                ...(data.gameState?.waitForBB !== undefined && { waitForBB: data.gameState.waitForBB }),
                                ...(data.gameState?.postOrWaitOptions && {
                                    postOrWaitOptions: {
                                        canPost: Boolean(data.gameState.postOrWaitOptions.canPost),
                                        postAmount: Number(data.gameState.postOrWaitOptions.postAmount),
                                        postType: String(data.gameState.postOrWaitOptions.postType),
                                        blinds: {
                                            small: room.smallBlind,
                                            big: room.bigBlind
                                        }
                                    }
                                })
                            },
                            stoodAt: data.stoodAt || null,
                            normalBuyinResetAt: data.normalBuyinResetAt || null,
                            timeUntilNormalBuyinResets: data.timeUntilNormalBuyinResets || null,
                            currency: data.currency,
                            currencyType: data.currencyType,
                            exchangeRate: data.exchangeRate,
                            exchangeCurrencyValue: data.exchangeCurrencyValue,
                        },
                        showSitOptionsModal: true
                    };
                }
                return room;
            })
        }));
    };

    public requestSit = (
        roomId: string,
        seatIndex: number,
        buyInAmount: number,
        postOrWaitChoice?: 'post' | 'wait'
    ) => {
        this.connection?.send('room', 'sit', {
            roomId,
            seatIndex,
            buyInAmount,
            ...(postOrWaitChoice && { postOrWaitChoice })
        }, roomId);
    };

    public handlePostOrWaitOptions = (data: any) => {
        const options = data.options;

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) =>
                room.roomId === data.roomId
                    ? {
                        ...room,
                        sitOptions: {
                            ...room.sitOptions,
                            seatIndex: room.sitOptions?.seatIndex ?? 0,
                            buyInLimits: room.sitOptions?.buyInLimits ?? { min: 0, max: 0 },
                            gameState: {
                                isGameActive: true,
                                requiresChoice: true,
                                ...(room.sitOptions?.gameState?.waitForBB !== undefined && { waitForBB: room.sitOptions.gameState.waitForBB }),
                                postOrWaitOptions: {
                                    canPost: Boolean(options.canPost),
                                    postAmount: Number(options.postAmount),
                                    postType: String(options.postType),
                                    blinds: room.sitOptions?.gameState?.postOrWaitOptions?.blinds ?? {
                                        small: room.smallBlind || 0,
                                        big: room.bigBlind || 0
                                    }
                                }
                            }
                        },
                        showSitOptionsModal: true
                    }
                    : room
            )
        }));
    };

    public handleBalanceWarning = (data: any) => {

        this.model.app.setState((prevState) => {
            const updatedRooms = prevState.joinedRooms.map((room) => {
                if (room.roomId === data.roomId) {
                    return {
                        ...room,
                        sitOptions: {
                            seatIndex: -1,
                            buyInLimits: {
                                min: data.limits.minBuyIn,
                                max: data.limits.maxBuyIn
                            },
                            gameState: {
                                isGameActive: true,
                                requiresChoice: false,
                                balanceWarning: {
                                    type: data.type,
                                    currentBalance: data.currentBalance,
                                    requiredAmount: data.requiredAmount,
                                    recommendedAmount: data.recommendedAmount,
                                    willSitOut: data.willSitOut,
                                    limits: data.limits
                                }
                            }
                        },
                        showSitOptionsModal: true
                    };
                }
                return room;
            });

            return { joinedRooms: updatedRooms };
        });
    };

    public handleShowCardsOption = (data: any) => {
        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) =>
                room.roomId === data.roomId
                    ? {
                        ...room,
                        showCardsOptions: {
                            canShow: data.canShow,
                            isMuckEnabled: data.isMuckEnabled,
                            opponents: data.opponents,
                            cards: data.cards
                        }
                    }
                    : room
            )
        }));
    };

    public requestShowCardsOption = (roomId: string, cards: string[]) => {
        this.connection?.send('room', 'playerShowCards', {
            roomId,
            cards
        });

        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) =>
                room.roomId === roomId
                    ? {
                        ...room,
                        showCardsOptions: undefined
                    }
                    : room
            )
        }));
    };

    public requestBalanceOptions = (roomId: string, userId: string) => {
        this.connection?.send('room', 'requestBalanceOptions', { roomId, userId }, roomId);
    }

    public handleBalanceOptionsResponse = (roomId: string, data: any) => {

        this.model.app.setState((prevState: any) => {
            const updatedRooms = prevState.joinedRooms.map((room: any) =>
                room.roomId === roomId
                    ? {
                        ...room,
                        showSitOptionsModal: true,
                        sitOptions: {
                            seatIndex: -1,
                            buyInLimits: {
                                min: data.buyInLimits?.minBuyIn ?? data.buyInLimits?.min ?? data.limits?.minBuyIn ?? data.limits?.min,
                                max: data.buyInLimits?.maxBuyIn ?? data.buyInLimits?.max ?? data.limits?.maxBuyIn ?? data.limits?.max
                            },
                            gameState: {
                                isGameActive: true,
                                requiresChoice: false,
                                balanceWarning: {
                                    type: 'shouldReload',
                                    currentBalance: data.currentBalance,
                                    requiredAmount: data.requiredTopUp,
                                    recommendedAmount: data.fixedBuyIn,
                                    willSitOut: false,
                                    limits: data.buyInLimits || data.limits
                                }
                            }
                        }
                    }
                    : room
            );

            return { joinedRooms: updatedRooms };
        });
    }

    public requestTopUpNextHand = (roomId: string) => {
        this.connection?.send('room', 'topUpNextHand', { roomId }, roomId);
    }

    public requestTopUpAlways = (roomId: string) => {
        this.connection?.send('room', 'topUpAlways', { roomId }, roomId);
    }

    public requestCancelTopUp = (roomId: string) => {
        this.connection?.send('room', 'cancelTopUp', { roomId }, roomId);
    }

    public requestSitOutNextHand = (roomId: string) => {
        this.connection?.send('room', 'sitOutNextHand', { roomId }, roomId);
    }

    public requestCancelSitOutNextHand = (roomId: string) => {
        this.connection?.send('room', 'cancelSitOutNextHand', { roomId }, roomId);
    }

    public requestSitOutNextBb = (roomId: string) => {
        this.connection?.send('room', 'sitOutNextBb', { roomId }, roomId);
    }

    public requestCancelSitOutNextBb = (roomId: string) => {
        this.connection?.send('room', 'cancelSitOutNextBb', { roomId }, roomId);
    }

    public requestSitBackIn = (roomId: string) => {
        this.connection?.send('room', 'sitBackIn', { roomId }, roomId);
    }

    public updateWalletBalance = (data: any) => {
        this.model.app.setState((prevState) => ({
            user: { ...prevState.user, balance: data.amount }
        }));
    }

    public updateUserTickets = (tickets: any[]) => {
        const nextTickets = Array.isArray(tickets) ? tickets : [];
        this.model.app.setState((prevState) => ({
            user: { ...prevState.user, tickets: nextTickets }
        }));
    }

    public handleBalanceUpdateFailed = (data: any) => {
        console.error('Balance update failed in room:', data.roomId);
        // Update room state to show buyin modal with balance warning
        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) =>
                room.roomId === data.roomId
                    ? {
                        ...room,
                        showSitOptionsModal: true,
                        sitOptions: {
                            seatIndex: -1,
                            buyInLimits: {
                                min: data.limits?.minBuyIn || room.minBuyIn,
                                max: data.limits?.maxBuyIn || room.maxBuyIn
                            },
                            gameState: {
                                isGameActive: true,
                                requiresChoice: false,
                                balanceWarning: {
                                    type: 'notEnoughBalance',
                                    currentBalance: data.currentBalance || 0,
                                    requiredAmount: data.requiredAmount,
                                    recommendedAmount: data.recommendedAmount,
                                    willSitOut: false,
                                    limits: {
                                        minBuyIn: data.limits?.minBuyIn || room.minBuyIn,
                                        maxBuyIn: data.limits?.maxBuyIn || room.maxBuyIn
                                    }
                                }
                            }
                        }
                    }
                    : room
            )
        }));
    };

    private getTournamentEliminationMessage = (data: any, room?: any, position?: number | null): string => {
        if (typeof data?.message === 'string' && data.message) {
            return data.message;
        }

        const tournamentName = data?.tournamentName ||
            data?.params?.name ||
            data?.name ||
            room?.tournamentName ||
            room?.name ||
            t('Notifications:tournament.defaultName');
        const resolvedPosition = typeof position === 'number'
            ? position
            : (typeof data?.params?.position === 'number' ? data.params.position : null);

        if (resolvedPosition !== null) {
            return t('Notifications:tournament.eliminated', {
                position: resolvedPosition,
                name: tournamentName
            });
        }

        return t('Notifications:tournament.eliminatedNoPosition', { name: tournamentName });
    }
    
    public handleServerMessage = (data: any) => {
        if (typeof data === 'string') {
            this.showNotification(data, {
                variant: 'info',
                persist: false,
                autoHideDuration: 5000
            });
            return;
        }

        // Resolve message: prefer messageKey (i18n) over hardcoded message,
        // but fall back to `data.message` if a translation is missing.
        let resolvedMessage: string;
        if (data.messageKey) {
            //For now we only use the no-ante version for blind notifications until AoF mtt is implemented
            let messageKey = data.messageKey;
            if (messageKey === 'tournament.blindIncrease') {
                messageKey = 'tournament.blindIncreaseNoAnte';
            } else if (messageKey === 'tournament.levelUp') {
                messageKey = 'tournament.levelUpNoAnte';
            }
            const i18nKey = `Notifications:${messageKey}`;
            const params = data.params || {};
            const translated = t(i18nKey, params) as any;
            const isMissingTranslation =
                typeof translated !== 'string' ||
                translated === data.messageKey ||
                translated === i18nKey ||
                (typeof (i18n as any)?.exists === 'function' && !(i18n as any).exists(i18nKey, params));

            resolvedMessage = isMissingTranslation
                ? (data.message || data.data?.message || data.messageKey)
                : translated;
        } else {
            resolvedMessage = data.message || data.data?.message || 'Server Message';
        }

        const isEliminationMessage = isTournamentEliminationNotification(data);

        // Parse notification data
        // Note: spread data first, then override message with resolved translation
        const notificationData: NotificationData = {
            ...data,
            message: resolvedMessage,
            scope: data.scope || 'app',
            type: data.type,
            roomId: data.roomId,
            tournamentId: data.tournamentId,
            tournamentGameId: data.tournamentGameId,
            tournamentName: data.tournamentName,
            gameId: data.gameId,
            isRegistrationConfirmation: data.isRegistrationConfirmation,
            isEliminationMessage: data.isEliminationMessage
        };

        // Decode HTML entities when the server provides direct message text.
        if (!data.messageKey) {
            notificationData.message = notificationData.message
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, '&')
                .replace(/\\n/g, '');
        }

        // Get joined room IDs
        const joinedRoomIds = this.model.app.state.joinedRooms.map(room => room.roomId);

        // Route room-scoped notifications to the specific room
        if (notificationData.scope === 'room' && notificationData.roomId) {
            const isInRoom = joinedRoomIds.includes(notificationData.roomId);
            if (isInRoom) {
                this.updateRoomState({
                    roomId: notificationData.roomId,
                    state: {
                        pendingNotification: {
                            message: notificationData.message,
                            type: notificationData.type || 'SYSTEM_INFO',
                            timestamp: Date.now()
                        }
                    }
                });
                return;
            }
            // Room closed before notification arrived (race condition) — fall through to app-scope
        }

        const options = NotificationTemplateUtil.getSnackbarOptions(
            notificationData, 
            notificationData.message,
            this.closeSnackbar ? (key) => this.closeSnackbar!(key) : undefined
        );
        if (isEliminationMessage) {
            options.preventDuplicate = false;
        }
        this.showNotification(notificationData.message, options, isEliminationMessage);
    }

    public setGameId = (data: any) => {
        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map((room) =>
                room.roomId === data.roomId ? { ...room, gameId_: data.gameId } : room
            )
        }));
    }

    public signUpForTournament = (gameId: string) => {
        this.connection?.send('tournament', 'addPlayer', { gameId });
    }

    public unsignFromTournament = (gameId: string) => {
        this.connection?.send('tournament', 'removePlayer', { gameId });
    }

    public handleTournamentPlayerUnregistered = (data: any) => {
        const tournamentId = typeof data?.tournamentGameId === 'string'
            ? data.tournamentGameId
            : (typeof data?.tournamentId === 'string'
                ? data.tournamentId
                : (typeof data?.gameId === 'string' ? data.gameId : undefined));
        const userId = typeof data?.userId === 'string' ? data.userId : undefined;

        if (tournamentId && userId) {
            this.model.app.setState((prevState) => {
                let changed = false;
                const updatedTournaments = (prevState.lobbyTournament?.tournaments || []).map((tournament: any) => {
                    const ids = [tournament?.gameId, tournament?.tournamentGameId, tournament?.tournamentId]
                        .filter((id): id is string => typeof id === 'string' && id.length > 0);
                    if (!ids.includes(tournamentId)) return tournament;

                    const playerList = Array.isArray(tournament.playerList) ? tournament.playerList : undefined;
                    const registeredPlayers = Array.isArray(tournament.registeredPlayers) ? tournament.registeredPlayers : undefined;
                    const nextPlayerList = playerList?.filter((player: any) => player?.userId !== userId);
                    const nextRegisteredPlayers = registeredPlayers?.filter((player: any) => player?.userId !== userId);
                    const removedFromPlayerList = Boolean(playerList && nextPlayerList && nextPlayerList.length !== playerList.length);
                    const removedFromRegisteredPlayers = Boolean(registeredPlayers && nextRegisteredPlayers && nextRegisteredPlayers.length !== registeredPlayers.length);

                    if (!removedFromPlayerList && !removedFromRegisteredPlayers) return tournament;

                    changed = true;
                    const nextTournament: any = { ...tournament };
                    if (nextPlayerList) {
                        nextTournament.playerList = nextPlayerList;
                    }
                    if (nextRegisteredPlayers) {
                        nextTournament.registeredPlayers = nextRegisteredPlayers;
                    }
                    if (typeof tournament.registeredPlayerCount === 'number') {
                        nextTournament.registeredPlayerCount = Math.max(0, tournament.registeredPlayerCount - 1);
                    }
                    return nextTournament;
                });

                if (!changed) return null;
                return {
                    lobbyTournament: {
                        ...prevState.lobbyTournament,
                        tournaments: updatedTournaments
                    }
                } as Pick<IAppState, "lobbyTournament">;
            });
        }

        this.requestLobbyUpdate();
    }

    public registerForTournamentWithTicket = (gameId: string, ticketId: string) => {
        this.connection?.send('tournament', 'registerWithTicket', { gameId, ticketId });
    }

    public handleTournamentError = (data: { message: string, gameId: string }) => {
        try {
            const message = (data?.message || '').toString();
            const gameId = data?.gameId;

            const isRebuyFundsError = /insufficient\s+funds\s+for\s+rebuy/i.test(message);
            const isRebuyGenericError = /rebuy\s+failed/i.test(message);

            if (isRebuyFundsError || isRebuyGenericError) {
                // Close/disable the rebuy modal and clear the offer
                const target = this.model.app.state.joinedRooms.find(r =>
                    r.isTournamentTable && (
                        r.tournamentGameId === gameId || r.tournamentId === gameId
                    )
                );

                if (target) {
                    const nextSit = {
                        ...(target.sitOptions || {}),
                        rebuyOffer: null
                    } as any;

                    this.updateRoomState({
                        roomId: target.roomId,
                        state: {
                            sitOptions: nextSit,
                            showSitOptionsModal: false
                        }
                    });
                }

                this.showNotification(t('Notifications:rebuy.notEnoughBalance'), {
                    variant: 'error',
                    autoHideDuration: 5000
                });
                return;
            }

            this.showNotification(message || t('Notifications:tournament.error'), {
                variant: 'error',
                autoHideDuration: 5000
            });
        } catch (e) {
            console.error('Failed to handle tournament error', e);
        }
    }

    // Attach rebuy offer to the currently focused tournament room (or any matching tournament)
    public attachRebuyOfferToTournamentRoom = (data: { tournamentId: string; chips: number; price: number; secondsToExpire: number }) => {
        const { tournamentId, chips, price, secondsToExpire } = data || ({} as any);
        const expiresAt = new Date(Date.now() + Math.max(0, Number(secondsToExpire) || 0) * 1000).toISOString();

        // Prefer the currently focused room if it's a tournament for this tournamentId
        const activeRoomId = this.getSingleViewRoomId();
        let targetRoomId: string | null = null;

        if (activeRoomId) {
            const activeRoom = this.model.app.state.joinedRooms.find(r => r.roomId === activeRoomId);
            const isSameTournament = activeRoom && (activeRoom.tournamentGameId === tournamentId || activeRoom.tournamentId === tournamentId);
            if (isSameTournament) {
                targetRoomId = activeRoomId;
            }
        }

        if (!targetRoomId) {
            const found = this.model.app.state.joinedRooms.find(r => r.isTournamentTable && (r.tournamentGameId === tournamentId || r.tournamentId === tournamentId));
            if (found) targetRoomId = found.roomId;
        }

        if (!targetRoomId) return;

        const currentRoom = this.model.app.state.joinedRooms.find(r => r.roomId === targetRoomId);
        const currentSit = currentRoom?.sitOptions || {} as any;
        const nextSit = {
            ...currentSit,
            rebuyOffer: {
                tournamentId,
                chips: Number(chips) || 0,
                price: Number(price) || 0,
                secondsToExpire: Number(secondsToExpire) || 0,
                expiresAt
            }
        };

        this.updateRoomState({
            roomId: targetRoomId,
            state: {
                sitOptions: nextSit,
                showSitOptionsModal: true
            }
        });
    }

    public getLobbyRooms() {
        return this.model.app.state.lobby.rooms;
    }

    public sendTournamentRebuyAccept = (gameId: string) => {
        this.connection?.send('tournament', 'rebuyAccept', { gameId });
    }

    public sendTournamentRebuyDecline = (gameId: string) => {
        this.connection?.send('tournament', 'rebuyDecline', { gameId });
    }

    public attachAddonOfferToTournamentRoom = (data: { tournamentId: string; endsAt: string; price: number; chips: number; maxPerPlayer: number }) => {
        try {
            const { tournamentId, endsAt, price, chips, maxPerPlayer } = data || ({} as any);

            // Prefer active single-view tournament room by tournament id
            const activeRoomId = this.getSingleViewRoomId();
            let targetRoomId: string | null = null;

            if (activeRoomId) {
                const activeRoom = this.model.app.state.joinedRooms.find(r => r.roomId === activeRoomId);
                const isSameTournament = activeRoom && (
                    activeRoom.tournamentGameId === tournamentId ||
                    (activeRoom as any).tournamentId === tournamentId
                );
                if (isSameTournament) {
                    targetRoomId = activeRoomId;
                }
            }

            if (!targetRoomId) {
                const found = this.model.app.state.joinedRooms.find(r => r.isTournamentTable && (
                    r.tournamentGameId === tournamentId || (r as any).tournamentId === tournamentId
                ));
                if (found) targetRoomId = found.roomId;
            }

            if (!targetRoomId) return;

            const currentRoom = this.model.app.state.joinedRooms.find(r => r.roomId === targetRoomId);
            const currentSit = currentRoom?.sitOptions || {} as any;
            const nextSit = {
                ...currentSit,
                addonOffer: {
                    tournamentId,
                    endsAt,
                    price: Number(price) || 0,
                    chips: Number(chips) || 0,
                    maxPerPlayer: Number(maxPerPlayer) || 0
                }
            };

            this.updateRoomState({
                roomId: targetRoomId,
                state: {
                    sitOptions: nextSit,
                    showSitOptionsModal: true
                }
            });
        } catch (e) {
            console.error('Failed to attach addon offer', e);
        }
    }

    public sendTournamentAddonAccept = (gameId: string) => {
        this.connection?.send('tournament', 'addonAccept', { gameId });
    }

    public sendTournamentAddonDecline = (gameId: string) => {
        this.connection?.send('tournament', 'addonDecline', { gameId });
    }

    public requestTournamentAddonGetOffer = (gameId: string) => {
        this.connection?.send('tournament', 'addonGetOffer', { gameId });
    }

    public handleTournamentAddonResult = (data: { tournamentId: string; userId: string; result: 'success' | 'error'; chipsGranted?: number; code?: string }) => {
        const { tournamentId, result, chipsGranted, code } = data || ({} as any);

        // Find any joined room matching this tournament
        const target = this.model.app.state.joinedRooms.find(r => r.isTournamentTable && (
            r.tournamentGameId === tournamentId || (r as any).tournamentId === tournamentId
        ));

        if (!target) return;

        if (result === 'success') {
            // Clear addonOffer; server will update balances
            const nextSit: any = { ...(target.sitOptions || {}), addonOffer: null };
            this.updateRoomState({ roomId: target.roomId, state: { sitOptions: nextSit } });
            try {
                const addChips = typeof chipsGranted === 'number' ? chipsGranted : 0;
                this.showNotification(t('Notifications:addon.purchased', { chips: addChips.toLocaleString() }), { variant: 'success', autoHideDuration: 4000 });
            } catch {}
        } else {
            // Map error code to message
            let message = t('Notifications:addon.purchaseFailed');
            switch (code) {
                case 'ADDON_WINDOW_CLOSED':
                    message = t('Notifications:addon.offerExpired');
                    break;
                case 'ADDON_MAX_REACHED':
                    message = t('Notifications:addon.limitReached');
                    break;
                case 'INSUFFICIENT_FUNDS':
                    message = t('Notifications:addon.insufficientFunds');
                    break;
                case 'TX_ERROR':
                    message = t('Notifications:addon.transactionError');
                    break;
            }
            try {
                this.showNotification(message, { variant: 'error', autoHideDuration: 5000 });
            } catch {}
            // Keep/clear offer depending on window; safest to keep UI but it will naturally expire
        }
    }

    public updateLobbyTournamentRankingsState = (data: any) => {
        this.model.app.setState((prevState) => {
            const prevTournaments = prevState.lobbyTournamentRankings?.tournaments || [];
            const newTournamentsRaw = Array.isArray(data.tournaments) ? data.tournaments : [];
            const userId = prevState.user?.id;
            
            // Skip state updates if incoming rankings are structurally identical.
            // Default enabled; can be disabled for debugging with VITE_ENABLE_RANKING_FILTERING="false".
            const enableFilteringEnv: any = (import.meta as any).env?.VITE_ENABLE_RANKING_FILTERING;
            const ENABLE_RANKING_FILTERING = enableFilteringEnv === 'false' ? false : true;

            const num0 = (v: any): number => (typeof v === 'number' ? v : 0);
            const strOrNull = (v: any): string | null => (typeof v === 'string' ? v : null);

            const standingsEqual = (a: any, b: any): boolean => {
                return (
                    a?.userId === b?.userId &&
                    a?.username === b?.username &&
                    num0(a?.balance) === num0(b?.balance) &&
                    num0(a?.rank) === num0(b?.rank) &&
                    (typeof a?.position === 'number' ? a.position : null) === (typeof b?.position === 'number' ? b.position : null) &&
                    strOrNull(a?.eliminationTime) === strOrNull(b?.eliminationTime) &&
                    strOrNull(a?.registrationTime) === strOrNull(b?.registrationTime) &&
                    (typeof a?.chipsBeforeElimination === 'number' ? a.chipsBeforeElimination : null) ===
                        (typeof b?.chipsBeforeElimination === 'number' ? b.chipsBeforeElimination : null)
                );
            };

            const progressionEqual = (a: any, b: any): boolean => {
                if (!a && !b) return true;
                if (!a || !b) return false;

                if (num0(a.version) !== num0(b.version)) return false;
                if (num0(a.totalPlayers) !== num0(b.totalPlayers)) return false;
                if (num0(a.totalTables) !== num0(b.totalTables)) return false;

                const aEvents = Array.isArray(a.events) ? a.events : [];
                const bEvents = Array.isArray(b.events) ? b.events : [];
                if (aEvents.length !== bEvents.length) return false;
                if (aEvents.length === 0) return true;

                const aLast = aEvents[aEvents.length - 1];
                const bLast = bEvents[bEvents.length - 1];
                return (
                    num0(aLast?.seq) === num0(bLast?.seq) &&
                    aLast?.type === bLast?.type &&
                    strOrNull(aLast?.timestamp) === strOrNull(bLast?.timestamp) &&
                    num0(aLast?.playersRemaining) === num0(bLast?.playersRemaining) &&
                    num0(aLast?.tablesActive) === num0(bLast?.tablesActive)
                );
            };

            const tournamentEqual = (prevT: any, nextT: any): boolean => {
                if (!prevT || !nextT) return false;
                if (prevT.gameId !== nextT.gameId) return false;

                // Prefer updatedAt if available (fast path)
                if ((prevT as any)?.updatedAt && (nextT as any)?.updatedAt) {
                    if ((prevT as any).updatedAt !== (nextT as any).updatedAt) return false;
                }

                if (prevT.tournamentName !== nextT.tournamentName) return false;
                if (prevT.templateId !== nextT.templateId) return false;
                if (prevT.status !== nextT.status) return false;
                if (prevT.type !== nextT.type) return false;
                if (num0(prevT.buyIn) !== num0(nextT.buyIn)) return false;
                if (num0(prevT.prizePool) !== num0(nextT.prizePool)) return false;
                if (prevT.startTime !== nextT.startTime) return false;
                if (num0(prevT.playersRegistered) !== num0(nextT.playersRegistered)) return false;
                if (num0(prevT.maxPlayers) !== num0(nextT.maxPlayers)) return false;

                if (strOrNull(prevT.lateRegistrationStartDate) !== strOrNull(nextT.lateRegistrationStartDate)) return false;
                if (strOrNull(prevT.lateRegistrationEndDate) !== strOrNull(nextT.lateRegistrationEndDate)) return false;

                if (num0(prevT.initialTotalChipBalance) !== num0(nextT.initialTotalChipBalance)) return false;
                if (num0(prevT.currentTotalChipBalance) !== num0(nextT.currentTotalChipBalance)) return false;
                if (num0(prevT.handCount) !== num0(nextT.handCount)) return false;

                if (Boolean(prevT.isFinal) !== Boolean(nextT.isFinal)) return false;
                if (num0(prevT.rebuyCount) !== num0(nextT.rebuyCount)) return false;
                if (num0(prevT.addonCount) !== num0(nextT.addonCount)) return false;
                if (num0(prevT.collectedFromRegistrations) !== num0(nextT.collectedFromRegistrations)) return false;
                if (num0(prevT.collectedFromRebuys) !== num0(nextT.collectedFromRebuys)) return false;
                if (num0(prevT.collectedFromAddons) !== num0(nextT.collectedFromAddons)) return false;
                if (num0(prevT.guaranteedPrizePool) !== num0(nextT.guaranteedPrizePool)) return false;

                if (num0(prevT.startingChips) !== num0(nextT.startingChips)) return false;
                if (num0(prevT.rebuyChips) !== num0(nextT.rebuyChips)) return false;
                if (num0(prevT.addonChips) !== num0(nextT.addonChips)) return false;

                if (!progressionEqual(prevT.tableStructureHistory, nextT.tableStructureHistory)) return false;

                const prevRankings = Array.isArray(prevT.rankings) ? prevT.rankings : [];
                const nextRankings = Array.isArray(nextT.rankings) ? nextT.rankings : [];
                if (prevRankings.length !== nextRankings.length) return false;
                for (let i = 0; i < prevRankings.length; i++) {
                    if (!standingsEqual(prevRankings[i], nextRankings[i])) return false;
                }

                return true;
            };

            if (ENABLE_RANKING_FILTERING && prevTournaments.length === newTournamentsRaw.length && prevTournaments.length > 0) {
                const prevMap = new Map(prevTournaments.map(t => [t.gameId, t]));
                let hasChanges = false;

                for (const nextT of newTournamentsRaw) {
                    const prevT = prevMap.get(nextT?.gameId);
                    if (!tournamentEqual(prevT, nextT)) {
                        hasChanges = true;
                        break;
                    }
                }

                if (!hasChanges) return null;
            }

            // Process tournaments
            const newTournaments = newTournamentsRaw.map((t: any) => ({
                ...t,
                initialTotalChipBalance: typeof t.initialTotalChipBalance === 'number' ? t.initialTotalChipBalance : 0,
                currentTotalChipBalance: typeof t.currentTotalChipBalance === 'number' ? t.currentTotalChipBalance : 0,
                handCount: typeof t.handCount === 'number' ? t.handCount : 0,
                // tableStructureHistory is now an object {version, totalPlayers, totalTables, events} or null
                tableStructureHistory: t.tableStructureHistory && typeof t.tableStructureHistory === 'object' ? t.tableStructureHistory : null,
                rankings: (t.rankings || []).map((s: any) => ({
                    ...s,
                    registrationTime: s.registrationTime || null,
                    chipsBeforeElimination: typeof s.chipsBeforeElimination === 'number' ? s.chipsBeforeElimination : null
                })),
            }));

            const nextRankingsState = {
                lobbyTournamentRankings: {
                    ...prevState.lobbyTournamentRankings,
                    type: 'tournamentRankings',
                    tournaments: newTournaments,
                },
            } as Pick<IAppState, "lobbyTournamentRankings">;

            // Derive elimination state for joined tournament rooms (so closing an eliminated table leaves the room).
            if (!userId) return nextRankingsState;

            const byTournamentId = new Map<string, { eliminated: boolean; eliminationTime: string | null; position: number | null }>();
            for (const tour of newTournaments) {
                const tid = tour?.gameId || tour?.tournamentId;
                if (!tid) continue;
                const rankings = Array.isArray(tour.rankings) ? tour.rankings : [];
                const r = rankings.find((x: any) => x?.userId === userId);
                if (!r) continue;
                const eliminationTime = typeof r.eliminationTime === 'string' ? r.eliminationTime : null;
                const position = typeof r.position === 'number' ? r.position : null;
                byTournamentId.set(tid, { eliminated: Boolean(eliminationTime), eliminationTime, position });
            }

            let joinedRoomsChanged = false;
            const updatedJoinedRooms = prevState.joinedRooms.map((room: any) => {
                if (!room?.isTournamentTable) return room;
                const tid = room?.tournamentGameId || room?.tournamentId;
                if (!tid) return room;
                const info = byTournamentId.get(tid);
                if (!info) return room;

                const prevElim = Boolean(room.tournamentEliminated);
                const prevTime = room.tournamentEliminationTime ?? null;
                const prevPos = room.tournamentPosition ?? null;
                if (prevElim === info.eliminated && prevTime === info.eliminationTime && prevPos === info.position) {
                    return room;
                }
                joinedRoomsChanged = true;
                return {
                    ...room,
                    tournamentEliminated: info.eliminated,
                    tournamentEliminationTime: info.eliminationTime,
                    tournamentPosition: info.position,
                };
            });

            if (!joinedRoomsChanged) return nextRankingsState;
            return {
                ...nextRankingsState,
                joinedRooms: updatedJoinedRooms,
            } as Pick<IAppState, "lobbyTournamentRankings" | "joinedRooms">;
        });
    }

    public notifyTournamentStarted = (data: any) => {
        this.showNotification(t('Notifications:tournament.started', { name: data.name }), {
            variant: 'success',
            autoHideDuration: 5000
        });
    }

    /**
     * Authoritative elimination signal emitted by the server (room-scoped).
     *
     * This is intentionally room/table-oriented (does not depend on lobby/rankings updates),
     * so the UI can reliably allow leaving/closing an MTT table after busting out.
     */
    public handleTournamentEliminationStatus = (data: any) => {
        const currentUserId = this.model.app.state.user?.id;
        const userId = data?.userId;
        if (typeof userId === 'string' && currentUserId && userId !== currentUserId) return;

        const roomId = typeof data?.roomId === 'string' && data.roomId ? data.roomId : undefined;
        const position = typeof data?.position === 'number' ? data.position : null;
        const tournamentGameId = typeof data?.tournamentGameId === 'string'
            ? data.tournamentGameId
            : (typeof data?.tournamentId === 'string' ? data.tournamentId : undefined);
        const tournamentId = typeof data?.tournamentId === 'string' ? data.tournamentId : tournamentGameId;
        if (!roomId && !tournamentGameId && !tournamentId) return;

        const notificationTimestamp = Date.now();

        this.model.app.setState((prevState) => {
            let changed = false;
            const tournamentFromLobby = (prevState.lobbyTournament?.tournaments || []).find((tournament: any) => {
                const ids = [tournament?.gameId, tournament?.tournamentGameId, tournament?.tournamentId]
                    .filter((id): id is string => typeof id === 'string' && id.length > 0);
                return [tournamentGameId, tournamentId].some((id) => typeof id === 'string' && ids.includes(id));
            });
            const messageData = tournamentFromLobby?.name && !data?.tournamentName
                ? { ...data, tournamentName: tournamentFromLobby.name }
                : data;
            const updatedJoinedRooms = prevState.joinedRooms.map((room: any) => {
                const result = applyTournamentEliminationStatusToRoom(room, data, {
                    notificationTimestamp,
                    buildMessage: (matchedRoom) => this.getTournamentEliminationMessage(messageData, matchedRoom, position)
                });
                if (result.changed) changed = true;
                return result.room;
            });

            if (!changed) return null;
            return { joinedRooms: updatedJoinedRooms };
        });
    }

    public notifyTournamentEnded = (data: any) => {
        const roomIdFromEvent: string | undefined = data.roomId;
        const tournamentGameIdFromEvent: string | undefined = data.tournamentGameId || data.tournamentId;
        const secondsToClose: number = typeof data.closeLastTableUponWinSeconds === 'number' ? data.closeLastTableUponWinSeconds : 20;

        const isJoinedRoom = roomIdFromEvent
            ? this.model.app.state.joinedRooms.some(r => r.roomId === roomIdFromEvent)
            : false;

        if (roomIdFromEvent && isJoinedRoom) {
            // Show tournament win modal on the affected joined room
            this.updateRoomState({
                roomId: roomIdFromEvent,
                state: {
                    showTournamentWinModal: true,
                    tournamentEndedInfo: {
                        tournamentId: data.tournamentId,
                        roomId: roomIdFromEvent,
                        winnerInformation: data.winnerInformation,
                        rankingData: data.rankingData,
                        closeLastTableUponWinSeconds: secondsToClose
                    },
                    tournamentCloseAt: Date.now() + secondsToClose * 1000
                }
            });

            // Inform the user
            try {
                const winnerName = data?.winnerInformation?.username || 'Winner';
                const winnerMessage = t('Notifications:tournament.endedWinner', { winnerName, seconds: secondsToClose });
                this.showNotification(winnerMessage, {
                    variant: 'info',
                    autoHideDuration: 6000,
                    ...this.createTournamentNotification(winnerMessage, 'trophy')
                });
            } catch {}
            return;
        }

        // Remove rooms by tournament id if any match.
        this.model.app.setState((prevState) => {
            const before = prevState.joinedRooms.length;
            const updatedJoinedRooms = prevState.joinedRooms.filter((room) => {
                if (!tournamentGameIdFromEvent) return true;
                return room.tournamentGameId !== tournamentGameIdFromEvent && (room as any).tournamentId !== tournamentGameIdFromEvent;
            });
            const removed = before !== updatedJoinedRooms.length;

            // If single view current room removed, navigate back to lobby
            const isSingleViewMode = prevState.user.section === AppSections.POKER_SINGLE_ROOM_VIEW;
            const currentRoomId = prevState.user.singleViewRoomId;
            let updatedUser = prevState.user;
            if (removed && isSingleViewMode && currentRoomId && !updatedJoinedRooms.some(room => room.roomId === currentRoomId)) {
                updatedUser = { ...prevState.user, singleViewRoomId: null, section: AppSections.LOBBY_AOF_FIAT };
            }
            return {
                ...prevState,
                joinedRooms: updatedJoinedRooms,
                user: updatedUser
            };
        }, () => {
            // Notify user
            try {
                const position = data?.position;
                const name = data?.name || t('Notifications:tournament.defaultName');
                const message = typeof position === 'number'
                    ? t('Notifications:tournament.endedWithPosition', { name, position })
                    : t('Notifications:tournament.ended', { name });
                this.showNotification(message, {
                    variant: 'info',
                    autoHideDuration: 7000,
                    ...this.createTournamentNotification(message, 'eliminated')
                });
            } catch {}
            if (this.model.app.state.user.section === AppSections.LOBBY_AOF_FIAT && !this.model.app.state.user.singleViewRoomId) {
                this.connection?.joinLobby('normal');
            }
        });
    }

    /**
     * Update a player's connection status (isDisconnected) in a room.
     * @param data { roomId: string, userId: string, isDisconnected: boolean }
     */
    public updatePlayerConnectionStatus = (data: { roomId: string, userId: string, isDisconnected: boolean }) => {
        const { roomId, userId, isDisconnected } = data;
        this.model.app.setState((prevState) => ({
            joinedRooms: prevState.joinedRooms.map(room =>
                room.roomId === roomId
                    ? {
                        ...room,
                        players: room.players.map(player =>
                            player.userId === userId
                                ? { ...player, isDisconnected }
                                : player
                        )
                    }
                    : room
            )
        }));
    }

    public updateUserSettings = (settings: { clientVolume?: number; notificationFrequency?: number; language?: string }) => {
        this.userSettingsManager.updateMultipleSettings(settings);
    }

    public updateUserSettingsSilent = (settings: { clientVolume?: number; notificationFrequency?: number; language?: string }) => {
        if (settings.clientVolume !== undefined) {
            this.userSettingsManager.updateSetting('clientVolume', settings.clientVolume, { immediate: false });
        }
        if (settings.notificationFrequency !== undefined) {
            this.userSettingsManager.updateSetting('notificationFrequency', settings.notificationFrequency, { immediate: false });
        }
        if (settings.language !== undefined) {
            this.userSettingsManager.updateSetting('language', settings.language, { immediate: true });
        }
    }

    public handleUserSettingsUpdated = (data: any) => {
        const result = this.userSettingsManager.handleServerUpdate(data);
        
        if (result.settings) {
            this.model.app.setState((prevState) => ({
                user: {
                    ...prevState.user,
                    pokerUserSettings: result.settings!
                }
            }));

            if (result.shouldShowNotification) {
                this.showNotification(t('Notifications:settings.updatedSuccessfully'), {
                    variant: 'success',
                    autoHideDuration: 3000
                });
            }
        }
    }

    /**
     * Handle failed user settings update from server
     * @param data { success: false, error: string }
     */
    public handleUserSettingsUpdateFailed = (data: any) => {
        const errorMessage = data.error || t('Notifications:settings.updateFailed');
        this.showNotification(errorMessage, {
            variant: 'error',
            autoHideDuration: 5000
        });
    }
} 
